import { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { accessSessionIdentity } from '../../../data/accessSessionIdentity'
import { QuickBooksForecastModelReview } from '../QuickBooksForecastModelReview'
import { NativeEconomicPolicyEditor } from './NativeEconomicPolicyEditor'
import { currentComponentStatements, currentForecastPreview, currentForecastRangeNotice, currentModeledCash, currentPreviewMatches, isCurrentForecast, mergeCurrentForecast } from './currentForecast'
import { Download, Maximize2, Minimize2 } from 'lucide-react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  CfoInsightsResponse,
  MetricComparisonKind,
  MetricComparisonResponse,
  QuickBooksFpaForecastGrain,
  QuickBooksFpaForecastLineMetadata,
  QuickBooksFpaForecastValueCase,
  QuickBooksFpaMlForecastArtifactsEnsureResponse,
  QuickBooksFpaMlForecastPackageSnapshotResponse,
  QuickBooksFpaMlForecastVsActualResponse,
  QuickBooksFpaMlDriverAssumptionResponse,
  QuickBooksFpaMlDriverPreviewLine,
  QuickBooksFpaMlScenarioAutopilotResponse,
  QuickBooksFpaMlStatementForecastResponse,
  QuickBooksFpaMlUserDelta,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
  QuickBooksCashPlanningWeekSummary,
  QuickBooksCfoStatementsRenderContractResponse,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import {
  clearForecastStudioSnapshotCache,
  FORECAST_STUDIO_BACKGROUND_ARTIFACT_TIMEOUT_MS,
  forecastStudioPerf,
  forecastStudioSnapshotCacheKey,
  forecastStudioSnapshotSignature,
  isDuckDbAccessHandleConflict,
  loadForecastStudioSnapshotCache,
  queryForecastLineExplain,
  releaseForecastPackageScopedTables,
  saveForecastStudioSnapshotCache,
  snapshotHasParquetArtifacts,
  warmForecastPackageForQueries,
  warmForecastSnapshotInDuckDb,
  warmUserDeltasInDuckDb,
  type ForecastArtifactKey,
} from './forecastArtifactsDuckDb'
// Re-exported for existing imports/tests; the implementation now lives in the
// shared artifact module.
export { forecastPackageScopedTableName } from './forecastArtifactsDuckDb'
import { CfoInteriorPage } from './CfoInteriorPage'
import { useCfoInlineGlDetail } from './CfoGlDetailModal'
import { CfoSurfaceBeliefs } from './CfoSurfaceBeliefs'
import { CfoDepthSection } from './CfoDepthSection'
import { CfoAiRead, CfoCardSkeletons, CfoControlRow, CfoStatementTable, type CfoStatementGlDetailCell } from './CfoPagePrimitives'
import { CfoForecastExplainDrawer, type CfoForecastExplainHistoryPoint } from './CfoForecastExplainDrawer'
import { forecastNodeReviewStates, type ForecastReviewState } from './forecastExplain'
export {
  backtestPlainLanguage,
  forecastNodeReviewStates,
  modelFamilyPlainLanguage,
  parseDriverDecomposition,
} from './forecastExplain'
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import { formatAsOf, formatCurrencyCompact, formatCurrencyExact, formatSignedCurrency, trimStr } from './cfoFormat'
import { checkProfitAndLossRenderFormulaIntegrity } from './cfoForecastIntegrity'
import { ForecastPacingPanel } from './ForecastPacingPanel'
import { ActivePlanVariancePanel } from './ActivePlanVariancePanel'
import {
  buildPacingModel,
  buildPacingPeriod,
  statementValueForMonth,
  type PacingLineInput,
  type PacingModel,
} from './forecastPacing'
import { CFO_SURFACES } from './cfoTelemetry'
import { startCfoCheckout } from './cfoBilling'
import { CfoProUpgradeNotice } from './CfoProUpgradeNotice'
import { cfoProFeaturesAvailable, useCfoBootstrap } from './useCfoBootstrap'
import { CfoShellSelectChip, type CfoShellSelectOption } from './CfoPageShell'
import type { CfoNavProfile } from './cfoNav'
import {
  defaultForecastAnchor,
  degradedCashNotice,
  FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS,
  FORECAST_STUDIO_DEFAULT_FORECAST_MONTHS,
  FORECAST_STUDIO_ENSURE_MAX_POLLS,
  FORECAST_STUDIO_ENSURE_POLL_MS,
  FORECAST_STUDIO_ENSURE_TIMEOUT_MS,
  FORECAST_STUDIO_MAX_FORECAST_MONTHS,
  FORECAST_STUDIO_SNAPSHOT_TIMEOUT_MS,
  forecastCacheStateForPage,
  forecastSnapshotMatchesAnchor,
  forecastStudioModelHistoryStartPeriod,
  forecastStudioPackageBuildNotice,
  forecastStudioPackageBuildState,
  forecastStudioRenderedPackageLabel,
  forecastStudioSnapshotMode,
  forecastStudioVisibleActualHistoryError,
  forecastStudioVisibleHistoryStartPeriod,
  addDaysIso,
  monthOffsetIso,
  monthStartIso,
  weekStartIso,
  shouldLoadForecastAnalyticsSidecars,
  type ForecastBuildFailureActionKind,
  type ForecastStudioPackageBuildState,
} from './forecastBuildState'
export {
  defaultForecastAnchor,
  degradedCashNotice,
  forecastBuildFailureGuidance,
  forecastCacheStateForPage,
  forecastSnapshotMatchesAnchor,
  forecastStudioPackageBuildNotice,
  forecastStudioPackageBuildState,
  forecastStudioRenderedPackageLabel,
  forecastStudioVisibleHistoryStartPeriod,
  forecastStudioVisibleActualHistoryError,
  shouldLoadForecastAnalyticsSidecars,
  type ForecastStudioPackageBuildState,
} from './forecastBuildState'
import { CfoForecastBuildProgress } from './CfoForecastBuildProgress'
import {
  balanceSheetCashValues,
  buildDirectCashForecastFromSnapshot,
  forecastStatementFromDuckDb,
  isDirectCashForecast,
  loadDirectCashActualDetailStatementsFromGlDetail,
  loadDirectCashForecastFromDuckDb,
  loadForecastCashDailyFromDuckDb,
  snapshotSupportsMonthlyLineMetadata,
  type DirectCashActualDetailStatements,
  type StatementKind,
} from './forecastDirectCash'
export {
  buildDirectCashActualGlDetailSql,
  buildDirectCashActualStatementFromRows,
  buildMappedDetailActualBalanceSheetStatement,
  buildMappedDetailActualBasisStatement,
  duckDbPreviewRowsAsObjects,
  forecastPeriodsFromSnapshot,
} from './forecastDirectCash'
import {
  applyForecastDriverDeltas,
  computeStatementDiff,
  createTrailingDebounce,
  driverNumber,
  forecastDriverMetricsFromStatement,
  hasDriverOverrides,
  hasStoredDriverAssumptions,
  isDriverOverride,
  metricValue,
  observedDriverAssumptions,
  revertAllDriverAssumptions,
  revertDriverAssumption,
  statementLineValue,
  stripDriverOverrides,
} from './forecastDriverEngine'
export {
  applyCashDriverDeltas,
  applyForecastDriverDeltas,
  computeStatementDiff,
  createTrailingDebounce,
  forecastDriverMetricsFromStatement,
  hasDriverOverrides,
  observedDriverAssumptions,
  revertAllDriverAssumptions,
  revertDriverAssumption,
  stripDriverOverrides,
} from './forecastDriverEngine'
import {
  applyServerDriverPreviewToStatement,
  createRequestNonce,
  describeWhatIfAssumptions,
  packageScenarioKeys,
  parseScenarioBandMeta,
  parseWhatIfPrompt,
  scenarioChipLabel,
} from './forecastScenarios'
export {
  applyServerDriverPreviewToStatement,
  createRequestNonce,
  describeWhatIfAssumptions,
  packageScenarioKeys,
  parseScenarioBandMeta,
  parseWhatIfPrompt,
  scenarioChipLabel,
} from './forecastScenarios'
import {
  mergeActualAndForecastStatements,
  sliceStatementDisplayWindow,
  withDirectCashRollForward,
} from './forecastStatementAssembly'
export {
  mergeActualAndForecastStatements,
  sliceStatementDisplayWindow,
  withDirectCashRollForward,
} from './forecastStatementAssembly'
import {
  finiteForecastValues,
  forecastBandLabel,
  ForecastTrendPanel,
  type ForecastTrendMetric,
  type ForecastTrendPointKind,
} from './ForecastTrendPanel'
import {
  forecastAccuracyStatementLabel,
  forecastAccuracySummary,
  forecastBandSource,
  readinessFromValidation,
} from './forecastAccuracy'
export { forecastAccuracySummary, forecastBandSource } from './forecastAccuracy'
import { buildCfoForecastExcelFileName, writeCfoForecastWorkbookExcelBytes } from './cfoForecastExcelExport'
import { assembleCfoForecastWorkbookInput } from './cfoForecastExportOrchestrator'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { buildCfoInstantCardFromContext } from './analysis/instantCards'
import type { CfoInstantCard, CfoInstantCardComparisonRow } from './analysis/types'

const QuickBooksForecastInsightsWorkspace = lazy(() =>
  import('../QuickBooksForecastInsightsWorkspace').then((m) => ({ default: m.QuickBooksForecastInsightsWorkspace })),
)

const SURFACE = CFO_SURFACES.forecast
const FORECAST_REQUIRED_COMPARISONS: MetricComparisonKind[] = ['mom', 'l3m_vs_prior3m', 'ytd', 'yoy']

export type CfoForecastPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  selectedEndPeriod?: string | null
  onSelectedEndPeriodChange?: (value: string | null) => void
  periodOptions?: CfoShellSelectOption[]
  comparisonKind?: MetricComparisonKind
  onComparisonKindChange?: (value: MetricComparisonKind) => void
  comparisonOptions?: CfoShellSelectOption[]
  onNavSelect: (navId: string) => void
  onOpenClassic: () => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

export const shouldUseServerForecastContractFirstPaint = (
  _runtimeProfile: CfoRuntimeProfile | null | undefined,
  _workerClient: AppWorkerClient | null | undefined,
): boolean => true

const GRAIN_OPTIONS = [
  { id: 'month', label: 'Monthly' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'daily', label: 'Daily' },
]

type ActualHistoryGrain = 'month' | 'weekly' | 'daily'

const ACTUAL_HISTORY_DEFAULTS: Record<ActualHistoryGrain, number> = {
  month: 3,
  weekly: 13,
  daily: 91,
}

const actualHistoryGrainFor = (grain: QuickBooksFpaForecastGrain | string): ActualHistoryGrain =>
  grain === 'weekly' || grain === 'daily' ? grain : 'month'

const normalizeActualHistoryPeriodCount = (value: number | string): number => {
  const parsed = Math.floor(Number(value))
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0
}

const forecastFiniteNumber = (value: unknown): number | null => {
  const next = Number(value)
  return Number.isFinite(next) ? next : null
}

const forecastPctChangeText = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '-'
  const sign = value > 0 ? '+' : value < 0 ? '-' : ''
  return `${sign}${Math.abs(value * 100).toFixed(1)}%`
}

const forecastComparisonRows = (
  comparisons: MetricComparisonResponse | null,
  metricIds: string[],
): CfoInstantCardComparisonRow[] => {
  const metric = metricIds.map((id) => comparisons?.metrics.find((candidate) => candidate.id === id)).find(Boolean) || null
  const byKind = new Map((metric?.comparisons || []).map((comparison) => [comparison.kind, comparison]))
  const row = (label: CfoInstantCardComparisonRow['label'], kind: MetricComparisonKind): CfoInstantCardComparisonRow => {
    const comparison = byKind.get(kind)
    const current = forecastFiniteNumber(comparison?.current_value)
    const prior = forecastFiniteNumber(comparison?.prior_value)
    const delta = current != null && prior != null ? current - prior : null
    return {
      label,
      current: current == null ? '-' : formatCurrencyCompact(current),
      prior: prior == null ? '-' : formatCurrencyCompact(prior),
      change: forecastPctChangeText(comparison?.delta_pct),
      tone: delta == null || Math.abs(delta) < 0.005 ? 'neutral' : delta > 0 ? 'good' : 'bad',
      unavailableReason: current == null || prior == null ? 'Not available in the forecast comparison artifact.' : null,
    }
  }
  return [
    row('Current month', 'mom'),
    row('Last 3 months', 'l3m_vs_prior3m'),
    row('YTD', 'ytd'),
    row('Prior year', 'yoy'),
  ]
}

const forecastTrendForInstantCard = (
  labels: string[],
  periods: Array<string | null>,
  values: Array<number | null>,
  kinds: ForecastTrendPointKind[],
) => {
  const points = values.slice(-12).map((value, offset) => {
    const sourceIndex = values.length - Math.min(12, values.length) + offset
    return {
      label: labels[sourceIndex] || periods[sourceIndex] || `P${offset + 1}`,
      period: periods[sourceIndex] || null,
      rawValue: forecastFiniteNumber(value),
      value: forecastFiniteNumber(value) == null ? '-' : formatCurrencyCompact(forecastFiniteNumber(value)),
      kind: kinds[sourceIndex] || 'forecast',
    }
  })
  return {
    label: points.length >= 12 ? '12-month forecast trend' : 'Available forecast trend',
    points,
    unavailable_reason: points.length >= 12 ? null : `Forecast statement has ${points.length} visible trend points for this metric.`,
  }
}

const forecastSourceFingerprint = (args: {
  packageId: string
  validationStatus: string
  actualAnchorPeriod: string
  statementKind: string
  grain: string
  endingCash: number | null
  values: Array<number | null>
}): string =>
  [
    SURFACE,
    trimStr(args.packageId),
    trimStr(args.validationStatus),
    trimStr(args.actualAnchorPeriod),
    trimStr(args.statementKind),
    trimStr(args.grain),
    forecastFiniteNumber(args.endingCash) ?? 0,
    args.values.slice(-4).map((value) => forecastFiniteNumber(value) ?? 0).join('|'),
  ].join(':')

export const buildForecastInstantCards = (args: {
  packageId: string
  validationStatus: string
  actualAnchorPeriod: string
  asOfLabel: string
  comparisonKind: MetricComparisonKind
  statementKind: StatementKind
  grain: QuickBooksFpaForecastGrain | string
  periodCount: number
  endingCash: number | null
  nearTermCash: number | null
  labels: string[]
  periods: Array<string | null>
  trendKinds: ForecastTrendPointKind[]
  endingCashValues: Array<number | null>
  comparisons: MetricComparisonResponse | null
}): CfoInstantCard[] => {
  const endingCash = forecastFiniteNumber(args.endingCash)
  const nearTermCash = forecastFiniteNumber(args.nearTermCash)
  if (endingCash == null && nearTermCash == null) return []
  const metricValue = endingCash ?? nearTermCash
  const metricId = endingCash == null ? 'modeled_net_cash' : 'projected_ending_cash'
  const metricLabel = endingCash == null ? 'Modeled net cash' : 'Projected ending cash'
  const sourceFingerprint = forecastSourceFingerprint({
    packageId: args.packageId,
    validationStatus: args.validationStatus,
    actualAnchorPeriod: args.actualAnchorPeriod,
    statementKind: args.statementKind,
    grain: args.grain,
    endingCash,
    values: args.endingCashValues,
  })
  return [
    buildCfoInstantCardFromContext({
      context: {
        workbookId: null,
        pageId: 'forecast',
        activeSection: SURFACE,
        period: args.actualAnchorPeriod || null,
        comparison: args.comparisonKind,
        asOfLabel: args.asOfLabel,
        metricId,
        metricLabel,
        sourceComponent: 'forecast_statement',
        sourceTables: ['quickbooks_fpa_ml_forecast_package', 'quickbooks_fpa_statement_render'],
        grounding: null,
        instantCard: null,
        selection: {
          selection_key: `forecast:${metricId}`,
          value: metricValue,
          metric_delta: nearTermCash,
          source_fingerprint: sourceFingerprint,
          artifact_ids: [args.packageId].filter(Boolean),
          statement_kind: args.statementKind,
          forecast_grain: args.grain,
          period_count: args.periodCount,
          instant_card_trend: forecastTrendForInstantCard(args.labels, args.periods, args.endingCashValues, args.trendKinds),
          instant_card_comparisons: forecastComparisonRows(args.comparisons, ['cash', 'ending_cash']),
          instant_card_insights: [
            endingCash != null
              ? `Projected ending cash is ${formatCurrencyCompact(endingCash)} after ${args.periodCount} ${args.grain === 'month' ? 'months' : args.grain === 'weekly' ? 'weeks' : 'days'} in the displayed forecast.`
              : `Modeled net cash is ${formatCurrencyCompact(nearTermCash)} in the displayed forecast window.`,
          ],
          instant_card_prescriptive_actions: [
            'Inspect the cash drivers and forecast rows with the largest movement before changing scenario assumptions.',
          ],
          instant_card_next_steps: [
            { kind: 'drilldown', label: 'Inspect cash drivers', prompt: 'Show the forecast cash drivers behind projected ending cash.', target: { surface: SURFACE, metricId } },
            { kind: 'open_surface', label: 'Open cash planning', prompt: 'Open cash planning for the forecast cash path.', target: { surface: CFO_SURFACES.cash, metricId } },
            { kind: 'compare', label: 'Compare periods', prompt: 'Compare projected cash against the required comparison periods.', target: { surface: SURFACE, metricId } },
          ],
          deterministic_read: endingCash != null
            ? `Projected ending cash is ${formatCurrencyCompact(endingCash)} in the displayed forecast.`
            : `Modeled net cash is ${formatCurrencyCompact(nearTermCash)} in the displayed forecast window.`,
        },
      },
      visual: null,
    }),
  ]
}

const STATEMENT_OPTIONS = [
  { id: 'cash_flow', label: 'Cash Flow' },
  { id: 'profit_and_loss', label: 'P&L' },
  { id: 'balance_sheet', label: 'Balance Sheet' },
]

const UNIT_OPTIONS: CfoShellSelectOption[] = [
  { id: 'currency', label: 'Amount' },
  { id: 'percent', label: '% of sales' },
  { id: 'currency_percent', label: 'Amount and %' },
]

const VALUE_CASE_OPTIONS = [
  { id: 'expected', label: 'Expected' },
  { id: 'high', label: 'High cash' },
  { id: 'low', label: 'Low cash' },
]

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const DRIVER_ASSUMPTION_SAVE_DEBOUNCE_MS = 600

// Server preview waits a bit longer than persistence so a typing burst issues
// one engine replay; the instant client recompute is always first paint.
const DRIVER_PREVIEW_DEBOUNCE_MS = 900

const patchDriver = (assumptions: any, key: string, value: number) => ({
  ...(assumptions || {}),
  [key]: {
    ...(assumptions?.[key] || {}),
    expected: value,
    source: 'user_override',
  },
})

const statementLabel = (statement: StatementKind) =>
  STATEMENT_OPTIONS.find((option) => option.id === statement)?.label || 'Cash Flow'

// Plain-English label for the package's internal `rollup_basis` so the customer
// surface never shows `materialized_parquet_artifacts` / `legacy_monthly_line_metadata`.
const forecastBasisLabel = (basis: string | undefined): string => {
  switch (trimStr(basis)) {
    case 'materialized_parquet_artifacts':
      return 'Live model'
    case 'legacy_monthly_line_metadata':
      return 'Monthly model'
    default:
      return 'Model'
  }
}

// Select one statement from the governed CFO render contract. This is the SAME
// canonical artifact the Financials page renders from, so sourcing the forecast's
// historical actuals here keeps the two pages citing identical numbers (same
// presentation-binding layout, same governed renderer) instead of the raw
// per-layout FPA render the forecast package used to use.
const governedStatementFromContract = (
  contract: QuickBooksCfoStatementsRenderContractResponse,
  statement: StatementKind,
): QuickBooksFpaStatementRender =>
  statement === 'balance_sheet'
    ? contract.balance_sheet
    : statement === 'cash_flow'
      ? contract.cash_flow
      : contract.profit_and_loss

const profitAndLossFormulaLabel = (check: string): string => {
  if (check === 'gross_profit') return 'Gross Profit'
  if (check === 'operating_income') return 'Operating Income'
  if (check === 'net_income') return 'Net Income'
  return check.replace(/_/g, ' ')
}

const forecastOptionLabel = (options: Array<{ id: string; label: unknown }>, id: string): string => {
  const label = options.find((option) => option.id === id)?.label
  return typeof label === 'string' ? label : String(label || id)
}

function downloadWorkbookBytes(args: { fileName: string; contentType?: string | null; bytes: ArrayBuffer | Uint8Array }): void {
  if (typeof document === 'undefined') return
  const fileName = (args.fileName || 'cfo-core-forecast.xlsx').trim() || 'cfo-core-forecast.xlsx'
  const blob = new Blob([args.bytes as BlobPart], { type: args.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  // Legacy IE/Edge path (preserves the filename directly).
  const legacy = (navigator as unknown as { msSaveOrOpenBlob?: (b: Blob, n: string) => void }).msSaveOrOpenBlob
  if (typeof legacy === 'function') { legacy(blob, fileName); return }
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  // Set the suggested name via both the property and the attribute — some
  // browser builds (notably automation/--no-sandbox Chrome) drop the blob
  // download name unless the attribute is explicitly present.
  anchor.download = fileName
  anchor.setAttribute('download', fileName)
  anchor.rel = 'noopener'
  anchor.style.position = 'fixed'
  anchor.style.left = '-10000px'
  document.body.appendChild(anchor)
  anchor.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
  // Keep the object URL alive well past download initiation — revoking too soon
  // can make the browser fall back to a blob-UUID filename for large workbooks.
  window.setTimeout(() => { try { anchor.remove() } catch { /* noop */ } URL.revokeObjectURL(url) }, 60000)
}

const forecastPeriodHeader = (grain: QuickBooksFpaForecastGrain | string) => (period: string | null | undefined) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(period || ''))
  if (!match) return formatAsOf(period).replace(/\s+\d{4}$/, '').slice(0, 3).toUpperCase()
  const month = Number(match[2])
  const day = Number(match[3])
  if (grain === 'weekly' || grain === 'daily') {
    const monthLabel = SHORT_MONTHS[month - 1] || match[2]
    return `${monthLabel} ${day}`
  }
  return (SHORT_MONTHS[month - 1] || match[2]).toUpperCase()
}

const forecastMonthYearHeader = (period: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})/.exec(String(period || ''))
  if (!match) return formatAsOf(period).replace(/\s+\d{4}$/, '')
  const month = Number(match[2])
  return `${SHORT_MONTHS[month - 1] || match[2]}'${match[1].slice(-2)}`
}

type ForecastBandStatements = {
  key: string
  low: QuickBooksFpaStatementRender | null
  high: QuickBooksFpaStatementRender | null
}

const compactTrendPeriodLabel = (period: string | null | undefined, grain: QuickBooksFpaForecastGrain | string): string => {
  if (grain === 'month') return forecastMonthYearHeader(period)
  return forecastPeriodHeader(grain)(period)
}

const rowTrendValues = (row: QuickBooksFpaStatementRow | null | undefined, indexes: number[]): Array<number | null> =>
  indexes.map((index) => {
    const value = Number(row?.values?.[index])
    return Number.isFinite(value) ? value : null
  })

const rowTrendValuesForPeriods = (
  row: QuickBooksFpaStatementRow | null | undefined,
  sourcePeriods: string[],
  periods: Array<string | null | undefined>,
): Array<number | null> => {
  const indexByPeriod = new Map(sourcePeriods.map((period, index) => [periodKeyForTrend(period), index]))
  return periods.map((period) => {
    const index = indexByPeriod.get(periodKeyForTrend(period))
    const value = index == null ? null : Number(row?.values?.[index])
    return Number.isFinite(value) ? value : null
  })
}

const periodKeyForTrend = (period: string | null | undefined): string => String(period || '').slice(0, 10)

const trendRow = (statement: QuickBooksFpaStatementRender | null | undefined, nodeId: string, labelPattern: RegExp): QuickBooksFpaStatementRow | null =>
  statement?.rows?.find((row) => row.node_id === nodeId)
  || statement?.rows?.find((row) => labelPattern.test(row.label || ''))
  || null

const rollingEndingCashTrendValues = (beginningCash: number | null | undefined, netValues: Array<number | null>): Array<number | null> => {
  if (beginningCash == null || !Number.isFinite(beginningCash)) return netValues.map(() => null)
  let rollingCash = beginningCash
  return netValues.map((value) => {
    if (value == null || !Number.isFinite(value)) return null
    rollingCash += value
    return rollingCash
  })
}

export const cumulativeEndingCashBandValues = ({
  endingCashValues,
  lowEndingCashValues,
  highEndingCashValues,
  netCashValues,
  lowNetCashValues,
  highNetCashValues,
  kinds,
}: {
  endingCashValues: Array<number | null>
  lowEndingCashValues: Array<number | null>
  highEndingCashValues: Array<number | null>
  netCashValues: Array<number | null>
  lowNetCashValues: Array<number | null>
  highNetCashValues: Array<number | null>
  kinds: ForecastTrendPointKind[]
}): { lowValues: Array<number | null>; highValues: Array<number | null> } => {
  const lowValues = endingCashValues.map(() => null as number | null)
  const highValues = endingCashValues.map(() => null as number | null)
  let cumulativeDownside = 0
  let cumulativeUpside = 0

  endingCashValues.forEach((endingCash, index) => {
    if (kinds[index] !== 'forecast') return
    const expectedEndingCash = Number(endingCash)
    if (!Number.isFinite(expectedEndingCash)) return

    const expectedNetCash = Number(netCashValues[index])
    const lowNetCash = Number(lowNetCashValues[index])
    const highNetCash = Number(highNetCashValues[index])
    if (Number.isFinite(expectedNetCash)) {
      const netCandidates = [lowNetCash, highNetCash].filter((value) => Number.isFinite(value))
      let periodDownside = 0
      let periodUpside = 0
      for (const candidate of netCandidates) {
        if (candidate < expectedNetCash) periodDownside = Math.max(periodDownside, expectedNetCash - candidate)
        if (candidate > expectedNetCash) periodUpside = Math.max(periodUpside, candidate - expectedNetCash)
      }
      cumulativeDownside += periodDownside
      cumulativeUpside += periodUpside
    }

    const endingCandidates = [
      Number(lowEndingCashValues[index]),
      Number(highEndingCashValues[index]),
    ].filter((value) => Number.isFinite(value))
    const directLow = endingCandidates.length ? Math.min(...endingCandidates) : null
    const directHigh = endingCandidates.length ? Math.max(...endingCandidates) : null
    const cumulativeLow = cumulativeDownside > 0 ? expectedEndingCash - cumulativeDownside : null
    const cumulativeHigh = cumulativeUpside > 0 ? expectedEndingCash + cumulativeUpside : null

    const lowCandidates = [directLow, cumulativeLow].filter((value): value is number => value != null && Number.isFinite(value))
    const highCandidates = [directHigh, cumulativeHigh].filter((value): value is number => value != null && Number.isFinite(value))
    if (!lowCandidates.length && !highCandidates.length) return
    lowValues[index] = lowCandidates.length ? Math.min(...lowCandidates) : expectedEndingCash
    highValues[index] = highCandidates.length ? Math.max(...highCandidates) : expectedEndingCash
  })

  return { lowValues, highValues }
}

type UnitMode = 'currency' | 'percent' | 'currency_percent'

export function CfoForecastPage(props: CfoForecastPageProps) {
  // Original actuals, authored inputs and review state belong to one workbook
  // and principal session. A changed owner retires the entire old read surface.
  const owner = JSON.stringify([trimStr(props.workbookId), accessSessionIdentity(props.appApi.getToken?.())])
  return <OwnedCfoForecastPage key={owner} {...props} />
}

function OwnedCfoForecastPage({
  appApi,
  workerClient = null,
  workbookId,
  selectedEndPeriod = null,
  onSelectedEndPeriodChange,
  periodOptions = [],
  comparisonKind = 'mom',
  onComparisonKindChange,
  comparisonOptions = [],
  onNavSelect,
  onOpenClassic,
  classicEscapes = false,
  navProfile = 'full',
  runtimeProfile,
}: CfoForecastPageProps) {
  const sessionIdentity = accessSessionIdentity(appApi.getToken?.())
  const [grain, setGrain] = useState<QuickBooksFpaForecastGrain | string>('month')
  // Default to the P&L statement table — the "24-month three-statement forecast"
  // object the marketing points at — instead of the Cash Flow chart view.
  const [statementKind, setStatementKind] = useState<StatementKind>('profit_and_loss')
  // Remember the statement the user last viewed on the month grain so switching
  // to weekly/daily (which only have a cash view) and back doesn't strand them on
  // Cash Flow.
  const monthStatementKindRef = useRef<StatementKind>('profit_and_loss')
  const handleStatementKindChange = (next: StatementKind) => {
    setStatementKind(next)
    if (grain === 'month') monthStatementKindRef.current = next
  }
  const [unitMode, setUnitMode] = useState<UnitMode>('currency_percent')
  const [statementFullscreen, setStatementFullscreen] = useState(false)
  const [valueCase, setValueCase] = useState<QuickBooksFpaForecastValueCase | string>('expected')
  const [actualHistoryByGrain, setActualHistoryByGrain] = useState<Record<ActualHistoryGrain, number>>(ACTUAL_HISTORY_DEFAULTS)
  const [futureMonths, setFutureMonths] = useState(FORECAST_STUDIO_DEFAULT_FORECAST_MONTHS)
  const [forecast, setForecast] = useState<QuickBooksFpaMlStatementForecastResponse | null>(null)
  const currentSource = isCurrentForecast(forecast?.current_source) ? forecast.current_source : null
  const nativeWritePending = useRef(false)
  const [nativeWriteBusy, setNativeWriteBusy] = useState(false)
  const nativeBuildScope = useRef('')
  nativeBuildScope.current = `${sessionIdentity}:${workbookId || ''}`
  const setNativeWritePending = (pending: boolean) => { nativeWritePending.current = pending; setNativeWriteBusy(pending) }

  const modeledCashQualified = !currentSource || currentSource.forecast_cash_flow_qualified
  const [currentPreviewPending, setCurrentPreviewPending] = useState(false)
  const [currentPreviewError, setCurrentPreviewError] = useState('')
  const [forecastSnapshot, setForecastSnapshot] = useState<QuickBooksFpaMlForecastPackageSnapshotResponse | null>(null)
  const [forecastBandStatements, setForecastBandStatements] = useState<ForecastBandStatements | null>(null)
  const [forecastCacheStatus, setForecastCacheStatus] = useState('')
  const [packageBuild, setPackageBuild] = useState<ForecastStudioPackageBuildState | null>(null)
  const [localCacheWarning, setLocalCacheWarning] = useState('')
  const [actualStatement, setActualStatement] = useState<QuickBooksFpaStatementRender | null>(null)
  const [actualDetailBasisStatement, setActualDetailBasisStatement] = useState<(QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null>(null)
  const [actualDetailBalanceSheetStatement, setActualDetailBalanceSheetStatement] = useState<(QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null>(null)
  const [actualBasisStatement, setActualBasisStatement] = useState<QuickBooksFpaStatementRender | null>(null)
  const [actualBalanceSheetStatement, setActualBalanceSheetStatement] = useState<QuickBooksFpaStatementRender | null>(null)
  const [assumptions, setAssumptions] = useState<QuickBooksFpaMlDriverAssumptionResponse | null>(null)
  const [autopilot, setAutopilot] = useState<QuickBooksFpaMlScenarioAutopilotResponse | null>(null)
  const [insights, setInsights] = useState<CfoInsightsResponse | null>(null)
  const [comparisons, setComparisons] = useState<MetricComparisonResponse | null>(null)
  const [forecastVsActual, setForecastVsActual] = useState<QuickBooksFpaMlForecastVsActualResponse | null>(null)
  // "Pacing vs forecast" panel: current in-progress period actuals vs that period's forecast.
  const [pacingForecastPnl, setPacingForecastPnl] = useState<QuickBooksFpaStatementRender | null>(null)
  const [pacingForecastCash, setPacingForecastCash] = useState<QuickBooksFpaStatementRender | null>(null)
  const [pacingActualPnl, setPacingActualPnl] = useState<QuickBooksFpaStatementRender | null>(null)
  const [pacingActualCashBalance, setPacingActualCashBalance] = useState<number | null>(null)
  const [pacingCashWeeks, setPacingCashWeeks] = useState<QuickBooksCashPlanningWeekSummary[] | null>(null)
  const [pacingAsOf, setPacingAsOf] = useState('')
  const [pacingLoading, setPacingLoading] = useState(false)
  const [pacingError, setPacingError] = useState('')
  const [accuracyOpen, setAccuracyOpen] = useState(false)
  const [baseAssumptions, setBaseAssumptions] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [actualLoading, setActualLoading] = useState(false)
  const [actualBasisLoading, setActualBasisLoading] = useState(false)
  const [actualStatementError, setActualStatementError] = useState('')
  const [actualDetailCashError, setActualDetailCashError] = useState('')
  const [actualBasisError, setActualBasisError] = useState('')
  const [excelExportBusy, setExcelExportBusy] = useState(false)
  const [excelExportStatus, setExcelExportStatus] = useState('')
  const [saving, setSaving] = useState('')
  const [error, setError] = useState('')
  const [forecastDeltas, setForecastDeltas] = useState<QuickBooksFpaMlUserDelta[]>([])
  const [staleDeltaKeys, setStaleDeltaKeys] = useState<string[]>([])
  const [explainTarget, setExplainTarget] = useState<{ nodeId: string; nodeLabel: string; period: string; statementType: string } | null>(null)
  const [explainLine, setExplainLine] = useState<QuickBooksFpaForecastLineMetadata | null>(null)
  const [explainLoading, setExplainLoading] = useState(false)
  const [explainLimited, setExplainLimited] = useState(false)
  const [explainError, setExplainError] = useState('')
  const [explainReviewBusy, setExplainReviewBusy] = useState(false)
  const [explainReviewStatus, setExplainReviewStatus] = useState('')
  const [ensureRetryNonce, setEnsureRetryNonce] = useState(0)
  const [autoAnchor, setAutoAnchor] = useState('')
  const [scenarioKey, setScenarioKey] = useState('base')
  const [scenarioBaseForecast, setScenarioBaseForecast] = useState<QuickBooksFpaMlStatementForecastResponse | null>(null)
  const [serverDriverPreview, setServerDriverPreview] = useState<{
    lines: QuickBooksFpaMlDriverPreviewLine[]
    computedInMs: number
    changedLineCount: number
  } | null>(null)
  const [whatIfText, setWhatIfText] = useState('')
  const [whatIfHint, setWhatIfHint] = useState('')
  const [whatIfLabel, setWhatIfLabel] = useState('')
  const [flaggedOnly, setFlaggedOnly] = useState(false)
  const [staleReviewOpen, setStaleReviewOpen] = useState(false)
  const [staleDeltaBusyId, setStaleDeltaBusyId] = useState('')
  const { bootstrap, cacheState, bootstrapLoading } = useCfoBootstrap(appApi, workbookId)
  // Pacing vs forecast is a Pro surface (the 13-week cash view is Essentials).
  const proFeatures = cfoProFeaturesAvailable(bootstrap)
  // Essentials tenants land on the 13-week cash (weekly) view rather than the
  // Pro month-grain statement forecast. One-time default once the plan is
  // known — if they later switch to Monthly they see the Pro upgrade notice.
  const essentialsGrainDefaultedRef = useRef(false)
  useEffect(() => {
    if (essentialsGrainDefaultedRef.current || bootstrapLoading) return
    if (!proFeatures && grain === 'month') {
      essentialsGrainDefaultedRef.current = true
      setGrain('weekly')
    } else {
      // Pro / legacy (unrestricted), or the user already moved off month.
      essentialsGrainDefaultedRef.current = true
    }
  }, [bootstrapLoading, proFeatures, grain])
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const actualHistoryGrain = actualHistoryGrainFor(grain)
  const actualHistoryPeriods = actualHistoryByGrain[actualHistoryGrain]
  const setActualHistoryPeriods = (value: number | string) => {
    const next = normalizeActualHistoryPeriodCount(value)
    setActualHistoryByGrain((current) => current[actualHistoryGrain] === next
      ? current
      : { ...current, [actualHistoryGrain]: next })
  }
  const forecastBinding = bootstrap?.bindings?.find(binding => trimStr(binding.workbook_id) === trimStr(workbookId)) || null
  const cfoAnalyticsReady = bootstrap?.cfo_analytics_ready
  const hasBootstrap = Boolean(bootstrap)
  const isQuickBooksProvider = !bootstrap?.accounting_provider || bootstrap.accounting_provider === 'quickbooks'
  const nativeForecastWorkbook = bootstrap?.current_source_mode === 'qbo_native_current'
    && Boolean(forecastBinding)
  const nativeReportingAnchor = nativeForecastWorkbook || Boolean(currentSource)
  // One compatibility release: older embedded clients and focused page tests
  // expose only the original QuickBooks-named methods. Production clients use
  // the provider-neutral methods; the fallback preserves the old contract
  // without sending non-QuickBooks workbooks to a QuickBooks route.
  const getStatementForecastCompatible = (
    wb: string,
    request: Parameters<ServerApiClient['getCfoStatementForecast']>[1],
  ): Promise<QuickBooksFpaMlStatementForecastResponse> => {
    const neutral = (appApi as any).getCfoStatementForecast
    return typeof neutral === 'function'
      ? neutral.call(appApi, wb, request)
      : appApi.getQuickBooksFpaMlStatementForecast(wb, request)
  }
  const ensureForecastArtifactsCompatible = (
    wb: string,
    request: Parameters<ServerApiClient['ensureCfoForecastArtifacts']>[1],
    options: Parameters<ServerApiClient['ensureCfoForecastArtifacts']>[2],
  ) => appApi.getCfoForecastArtifactStatus(wb, request, options)
  const requestForecastBuild = async () => {
    const wb = trimStr(workbookId)
    if (!wb || !selectedEndPeriod) return
    if (nativeForecastWorkbook || currentSource) {
      if (nativeWritePending.current) return
      setNativeWritePending(true)
      const owned = nativeBuildScope.current
      try {
        const queued = await appApi.runQuickBooksFpaMlForecast(wb, { scenario_key: 'base',
          forecast_start_period: monthOffsetIso(monthStartIso(selectedEndPeriod), 1) })
        if (nativeBuildScope.current !== owned || accessSessionIdentity(appApi.getToken?.()) !== sessionIdentity) return
        setSaving(queued.message || `Model fitting ${queued.status}; review the completed run before current scoring.`)
      } finally { setNativeWritePending(false) }
    } else {
      await appApi.ensureCfoForecastArtifacts(wb, { as_of_date: selectedEndPeriod, future_months: futureMonths })
    }
    setEnsureRetryNonce((nonce) => nonce + 1)
  }

  const actualAnchorPeriod = trimStr(
    selectedEndPeriod ||
    forecast?.package?.source_window_end ||
    forecast?.as_of_date ||
    insights?.as_of ||
    comparisons?.as_of ||
    '',
  )
  const { openGlDetail, glDetailModal } = useCfoInlineGlDetail({
    appApi,
    workerClient,
    workbookId,
    duckDbOpts,
    onNavSelect,
    period: actualAnchorPeriod || null,
  })
  const actualConnectionId = trimStr(
    forecast?.statement?.connection_id ||
    forecast?.package?.connection_id ||
    forecastSnapshot?.package?.connection_id ||
    forecastBinding?.connection_id ||
    '',
  )
  const actualLayoutVersionId = trimStr(
    forecast?.statement?.layout_version_id ||
    forecastSnapshot?.layout_nodes?.[0]?.layout_version_id ||
    forecastBinding?.layout_version_id ||
    '',
  )
  // Same layout the Financials page renders (regroup, rename, hide), read-only
  // here: the forecast statement follows it instead of showing every node.
  const actualLayout = useMemo(
    () => bootstrap?.layouts?.find((layout) => layout.id === actualLayoutVersionId) || null,
    [bootstrap?.layouts, actualLayoutVersionId],
  )
  const forecastPackageId = trimStr(currentSource?.authored_package_id || forecast?.package?.id || forecastSnapshot?.package?.id || '')
  const forecastPackageSourceWindowEnd = trimStr(forecast?.package?.source_window_end || forecastSnapshot?.package?.source_window_end || '')
  // Blocked/failed packages carry a regressed source window (their stale
  // trailing months were excluded), so deriving an anchor from one walks the
  // anchor backwards run after run. Only anchor-ready packages may cap it.
  const forecastPackageStatus = trimStr(forecast?.package?.status || forecastSnapshot?.package?.status || '')
  const anchorSafeSourceWindowEnd = forecastPackageStatus === 'ready' || forecastPackageStatus === 'partial'
    ? forecastPackageSourceWindowEnd
    : ''
  const selectedForecastPackageId = forecastPackageId && (!selectedEndPeriod || !forecastPackageSourceWindowEnd || forecastPackageSourceWindowEnd === selectedEndPeriod)
    ? forecastPackageId
    : ''
  const modelHistoryStartPeriod = forecastStudioModelHistoryStartPeriod(actualAnchorPeriod)
  const modelBalanceSheetHistoryStartPeriod = modelHistoryStartPeriod ? monthOffsetIso(modelHistoryStartPeriod, -1) : null
  const forecastScenarioKey = trimStr(currentSource?.scenario_key || forecastSnapshot?.scenario_key || forecastSnapshot?.package?.active_scenario_key || '') || 'base'

  const comparisonsRef = useRef<MetricComparisonResponse | null>(null)
  const forecastCacheRef = useRef<Map<string, QuickBooksFpaMlStatementForecastResponse>>(new Map())
  const snapshotCacheRef = useRef<Map<string, QuickBooksFpaMlForecastPackageSnapshotResponse>>(new Map())
  const forecastArtifactWarmInFlightRef = useRef<Map<string, Promise<void>>>(new Map())
  const warmedForecastArtifactKeysRef = useRef<Set<string>>(new Set())
  const warmedForecastPackageIdRef = useRef('')
  const forecastRef = useRef<QuickBooksFpaMlStatementForecastResponse | null>(null)
  const snapshotRequestInFlightRef = useRef<Map<string, Promise<QuickBooksFpaMlForecastPackageSnapshotResponse>>>(new Map())
  const actualCacheRef = useRef<Map<string, QuickBooksFpaStatementRender>>(new Map())
  // Dedupe the governed CFO render-contract fetch (shared by the actual-history
  // and cash-bridge basis loads below) so both consume one canonical pull.
  const governedStatementsRef = useRef<Map<string, Promise<QuickBooksCfoStatementsRenderContractResponse>>>(new Map())
  const actualDetailBasisInFlightRef = useRef<Map<string, Promise<DirectCashActualDetailStatements>>>(new Map())
  const baseAssumptionsKeyRef = useRef('')
  const explainWarmedPackageRef = useRef('')
  const serverForecastFirstPaint = shouldUseServerForecastContractFirstPaint(runtimeProfile, workerClient)
  useEffect(() => {
    comparisonsRef.current = comparisons
  }, [comparisons])
  useEffect(() => {
    forecastRef.current = forecast
  }, [forecast])

  useEffect(() => {
    // Weekly/daily grains only have a cash view; force it, but restore the user's
    // month statement when they return to the month grain instead of leaving them
    // stranded on Cash Flow.
    if (grain === 'daily' || grain === 'weekly') {
      setStatementKind('cash_flow')
    } else {
      setStatementKind(monthStatementKindRef.current)
    }
  }, [grain])

  // First-load smart anchor: when nothing is explicitly selected, anchor to
  // the most recent fully-closed month instead of the active package, and say
  // so via the anchor chip. Runs once; an explicit selection wins forever.
  // Waits for the active package so the anchor caps at the synced source
  // window — anchoring past it would select a month with no reconciled books
  // and dead-end on a build that cannot succeed until the next sync.
  const autoAnchorAttemptedRef = useRef(false)
  useEffect(() => {
    if (autoAnchorAttemptedRef.current) return
    if (trimStr(selectedEndPeriod)) {
      autoAnchorAttemptedRef.current = true
      return
    }
    if (!onSelectedEndPeriodChange || !periodOptions.length) return
    if (!anchorSafeSourceWindowEnd) return
    const anchor = defaultForecastAnchor(
      periodOptions,
      anchorSafeSourceWindowEnd,
      new Date().toISOString().slice(0, 10),
    )
    if (!anchor) return
    autoAnchorAttemptedRef.current = true
    setAutoAnchor(anchor)
    if (anchor.slice(0, 7) !== anchorSafeSourceWindowEnd.slice(0, 7)) {
      onSelectedEndPeriodChange(anchor)
    }
  }, [anchorSafeSourceWindowEnd, onSelectedEndPeriodChange, periodOptions, selectedEndPeriod])

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (statementFullscreen) {
      document.body.setAttribute('data-cfo-financials-fullscreen', 'true')
    } else {
      document.body.removeAttribute('data-cfo-financials-fullscreen')
    }
    return () => {
      document.body.removeAttribute('data-cfo-financials-fullscreen')
    }
  }, [statementFullscreen])

  useEffect(() => {
    let cancelled = false
    const sessionIsCurrent = () => !cancelled && accessSessionIdentity(appApi.getToken?.()) === sessionIdentity
    let onDemandBuildWasObserved = false
    const wb = trimStr(workbookId)
    if (!wb) {
      setLoading(false)
      setError('No workbook is bound to this view.')
      return
    }
    if (bootstrapLoading) {
      setLoading(true)
      setError('')
      return
    }
    setLoading(true)
    setError('')
    if (selectedEndPeriod) {
      setForecast(null)
      setForecastSnapshot(null)
      setForecastBandStatements(null)
      setForecastCacheStatus('')
      setLocalCacheWarning('')
    }
    const cacheKey = JSON.stringify({
      wb,
      sessionIdentity,
      grain,
      statementKind,
      valueCase,
      scenarioKey,
      selectedEndPeriod,
      futureMonths: grain === 'month' ? futureMonths : null,
    })
    // A previous ready response is never an authorization for a new read.
    const acceptCurrentForecast = (next: QuickBooksFpaMlStatementForecastResponse) => {
      if (!sessionIsCurrent()) return
      setPackageBuild(null)
      setForecastSnapshot(null)
      setForecastBandStatements(null)
      setForecast(next)
      setServerDriverPreview(null)
      setCurrentPreviewPending(false)
      setCurrentPreviewError('')
      setAssumptions({ package: null, assumptions: next.driver_assumptions,
        current_source: next.current_source })
      setForecastCacheStatus(next.current_source ? currentForecastRangeNotice(next.current_source) : 'Current source metadata is unavailable.')
      setLoading(false)
    }
    const loadCurrentForecast = () => getStatementForecastCompatible(wb, {
      scenario_key: scenarioKey, grain, statement_type: statementKind, value_case: valueCase,
      as_of_date: selectedEndPeriod, future_months: futureMonths,
    }).then(next => {
      if (!sessionIsCurrent()) return
      if (!isCurrentForecast(next.current_source)) throw new Error('The current source selection changed during the read. Reload this view.')
      acceptCurrentForecast(next)
    })
    if (nativeForecastWorkbook) {
      // The canonical read already returns current actuals, policy and model
      // availability. It does not need a second status/scoring read or package.
      void loadCurrentForecast().catch((err: any) => {
        if (!sessionIsCurrent()) return
        setPackageBuild(null)
        setForecast(null)
        setForecastSnapshot(null)
        setForecastCacheStatus('')
        setError(trimStr(err?.message || err) || 'Could not load the current forecast.')
        setLoading(false)
      })
      return () => { cancelled = true }
    }
    // Observe the prepared package for this period. Only an explicit build
    // action may enqueue processing; navigation and polling remain read-only.
    const ensureForecastArtifactsOnce = (): Promise<QuickBooksFpaMlForecastArtifactsEnsureResponse> =>
      ensureForecastArtifactsCompatible(wb, {
        as_of_date: trimStr(selectedEndPeriod),
        future_months: grain === 'month' ? futureMonths : FORECAST_STUDIO_DEFAULT_FORECAST_MONTHS,
      }, { timeoutMs: FORECAST_STUDIO_ENSURE_TIMEOUT_MS })
    // Anchor selections resolve their own artifact-backed package before any
    // snapshot/statement data is fetched; queued/running builds render as an
    // inline building state instead of an API error.
    const startForecastStudioLoad = (run: (ensuredPackageId: string | null) => void) => {
      if (!selectedEndPeriod) {
        setPackageBuild(null)
        if (grain === 'month' && !serverForecastFirstPaint) {
          void getStatementForecastCompatible(wb, { scenario_key: scenarioKey, grain,
            statement_type: statementKind, value_case: valueCase, future_months: futureMonths })
            .then(next => {
              if (!sessionIsCurrent()) return
              if (isCurrentForecast(next.current_source)) acceptCurrentForecast(next)
              else run(null)
            }).catch(err => {
              if (!sessionIsCurrent()) return
              setForecast(null); setError(trimStr(err?.message || err)); setLoading(false)
            })
        } else run(null)
        return
      }
      void (async () => {
        try {
          let ensured = await ensureForecastArtifactsOnce()
          if (!sessionIsCurrent()) return
          let polls = 0
          while (sessionIsCurrent() && (ensured.status === 'queued' || ensured.status === 'running') && polls < FORECAST_STUDIO_ENSURE_MAX_POLLS) {
            onDemandBuildWasObserved = true
            setPackageBuild(forecastStudioPackageBuildState(selectedEndPeriod, ensured))
            setForecast(null)
            setForecastSnapshot(null)
            setForecastBandStatements(null)
            setForecastCacheStatus('')
            setLoading(true)
            await new Promise((resolve) => setTimeout(resolve, FORECAST_STUDIO_ENSURE_POLL_MS))
            if (!sessionIsCurrent()) return
            ensured = await ensureForecastArtifactsOnce()
            polls += 1
          }
          if (!sessionIsCurrent()) return
          if (isCurrentForecast(ensured.current_source)) {
            await loadCurrentForecast()
            return
          }
          if (trimStr(ensured.status) === 'ready') {
            setPackageBuild(null)
            setLoading(true)
            run(trimStr(ensured.package_id) || null)
            return
          }
          setPackageBuild(forecastStudioPackageBuildState(selectedEndPeriod, ensured))
          setForecastCacheStatus('')
          setError('')
          setLoading(ensured.status === 'queued' || ensured.status === 'running')
        } catch (err: any) {
          if (!sessionIsCurrent()) return
          setPackageBuild(null)
          setForecast(null)
          setForecastSnapshot(null)
          setForecastCacheStatus('')
          setError(trimStr(err?.message || err) || 'Could not prepare the forecast package for the selected month.')
          setLoading(false)
        }
      })()
    }
    if (serverForecastFirstPaint) {
      const runServerForecastLoad = (_ensuredPackageId: string | null) => {
        const requestedMonths = grain === 'month' ? futureMonths : Math.max(3, futureMonths)
        getStatementForecastCompatible(wb, {
          scenario_key: scenarioKey === 'base' ? undefined : scenarioKey,
          grain,
          statement_type: statementKind,
          value_case: valueCase,
          as_of_date: selectedEndPeriod,
          future_months: requestedMonths,
          limit: statementKind === 'cash_flow' ? 20000 : 5000,
        })
          .then((serverForecast) => {
            if (!sessionIsCurrent()) return
            if (isCurrentForecast(serverForecast.current_source)) {
              acceptCurrentForecast(serverForecast)
              return
            }
            forecastCacheRef.current.set(cacheKey, serverForecast)
            setPackageBuild(null)
            setForecastSnapshot(null)
            setForecast(serverForecast)
            setForecastCacheStatus(
              onDemandBuildWasObserved
                ? 'Forecast ready — up to date with your books.'
                : 'Up to date with your books.',
            )
            setLoading(false)
          })
          .catch((err: any) => {
            if (!sessionIsCurrent()) return
            setPackageBuild(null)
            setForecast(null)
            setForecastSnapshot(null)
            setForecastCacheStatus('')
            setError(trimStr(err?.message || err) || 'Could not load the server forecast contract.')
            setLoading(false)
          })
      }
      startForecastStudioLoad(runServerForecastLoad)
      return () => {
        cancelled = true
      }
    }
    if (statementKind === 'cash_flow') {
      const snapshotMonths = Math.max(3, futureMonths)
      const snapshotMode = forecastStudioSnapshotMode(grain)
      const baseSnapshotKey = JSON.stringify({ wb, selectedEndPeriod, futureMonths: snapshotMonths, snapshotMode, scenarioKey, packageId: null })
      const durableSnapshotKey = forecastStudioSnapshotCacheKey(wb, selectedEndPeriod, snapshotMonths, snapshotMode, scenarioKey)
      const buildFromSnapshot = (snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse) => {
        if (grain === 'month') return null
        const localForecast = buildDirectCashForecastFromSnapshot(snapshot, grain, valueCase, selectedEndPeriod, grain === 'month' ? futureMonths : snapshotMonths)
        if (!localForecast) return null
        forecastCacheRef.current.set(cacheKey, localForecast)
        return localForecast
      }
      const cachedSnapshotCandidate = snapshotCacheRef.current.get(baseSnapshotKey)
        || loadForecastStudioSnapshotCache(durableSnapshotKey)
        || null
      // A snapshot cached under the old fallback behavior can hold a different
      // month's package; never paint it as the selected anchor.
      const cachedSnapshot = cachedSnapshotCandidate && forecastSnapshotMatchesAnchor(cachedSnapshotCandidate, selectedEndPeriod)
        ? cachedSnapshotCandidate
        : null
      if (cachedSnapshotCandidate && !cachedSnapshot) {
        snapshotCacheRef.current.delete(baseSnapshotKey)
        clearForecastStudioSnapshotCache(durableSnapshotKey)
      }
      if (cachedSnapshot) {
        snapshotCacheRef.current.set(baseSnapshotKey, cachedSnapshot)
        const localForecast = buildFromSnapshot(cachedSnapshot)
        if (localForecast && !selectedEndPeriod) {
          setForecastSnapshot(cachedSnapshot)
          setForecast(localForecast)
          setForecastCacheStatus('Using durable browser forecast package.')
          setLoading(false)
        }
      }
      const runSnapshotLoad = (ensuredPackageId: string | null) => {
        const requestPackageId = trimStr(ensuredPackageId) || null
        const snapshotKey = JSON.stringify({ wb, selectedEndPeriod, futureMonths: snapshotMonths, snapshotMode, scenarioKey, packageId: requestPackageId })
        const requestDurableSnapshotKey = forecastStudioSnapshotCacheKey(wb, selectedEndPeriod, snapshotMonths, snapshotMode, scenarioKey, requestPackageId)
        const cachedSnapshotCandidateForRequest = snapshotCacheRef.current.get(snapshotKey)
          || loadForecastStudioSnapshotCache(requestDurableSnapshotKey)
          || (!requestPackageId ? cachedSnapshot : null)
        const cachedSnapshotForRequest = cachedSnapshotCandidateForRequest && forecastSnapshotMatchesAnchor(cachedSnapshotCandidateForRequest, selectedEndPeriod)
          ? cachedSnapshotCandidateForRequest
          : null
        if (cachedSnapshotCandidateForRequest && !cachedSnapshotForRequest) {
          snapshotCacheRef.current.delete(snapshotKey)
          clearForecastStudioSnapshotCache(requestDurableSnapshotKey)
        }
        if (cachedSnapshotForRequest) snapshotCacheRef.current.set(snapshotKey, cachedSnapshotForRequest)
      let snapshotPromise = snapshotRequestInFlightRef.current.get(snapshotKey)
      const requestStartedAt = Date.now()
      if (snapshotPromise) {
        forecastStudioPerf('snapshot_request_joined', {
          workbook_id: wb,
          grain,
          statement_kind: statementKind,
          value_case: valueCase,
          as_of_date: selectedEndPeriod || null,
          future_months: snapshotMonths,
        })
      } else {
        forecastStudioPerf('snapshot_request_start', {
          workbook_id: wb,
          grain,
          statement_kind: statementKind,
          value_case: valueCase,
          as_of_date: selectedEndPeriod || null,
          future_months: snapshotMonths,
        })
        // Anchor requests load the ensured package directly; they never fall
        // back to the globally active package, which can be a different month.
        const loadSnapshotForAnchor = (asOfDate: string | null | undefined) => appApi.getQuickBooksFpaMlForecastPackageSnapshot(wb, {
          package_id: requestPackageId || undefined,
          scenario_key: scenarioKey === 'base' ? undefined : scenarioKey,
          as_of_date: asOfDate,
          grain,
          future_months: snapshotMonths,
          daily_limit: 50000,
        }, { timeoutMs: FORECAST_STUDIO_SNAPSHOT_TIMEOUT_MS })
        snapshotPromise = loadSnapshotForAnchor(selectedEndPeriod)
        snapshotRequestInFlightRef.current.set(snapshotKey, snapshotPromise)
        void snapshotPromise
          .finally(() => {
            if (snapshotRequestInFlightRef.current.get(snapshotKey) === snapshotPromise) {
              snapshotRequestInFlightRef.current.delete(snapshotKey)
            }
          })
          .catch(() => undefined)
      }
      snapshotPromise
        .then(async (snapshot) => {
          if (!sessionIsCurrent()) return
          const artifactBacked = snapshotHasParquetArtifacts(snapshot)
          const monthlyLineBacked = snapshotSupportsMonthlyLineMetadata(snapshot)
          // The loader only allocates daily/weekly from monthly line metadata
          // when the package has NO parquet artifacts — with artifacts present
          // it queries the real cash_daily table, so warm decisions and
          // branch dispatch must match that exactly.
          const bridgeFromMonthly = monthlyLineBacked && !artifactBacked
          forecastStudioPerf('snapshot_request_done', {
            workbook_id: wb,
            package_id: snapshot.package?.id || null,
            artifact_backed: artifactBacked,
            monthly_line_backed: monthlyLineBacked,
            cash_daily_rows: snapshot.cash_daily?.length || 0,
            line_metadata_rows: snapshot.line_metadata?.length || 0,
            layout_nodes: snapshot.layout_nodes?.length || 0,
            elapsed_ms: Date.now() - requestStartedAt,
          })
          if (cachedSnapshotForRequest && forecastStudioSnapshotSignature(cachedSnapshotForRequest) === forecastStudioSnapshotSignature(snapshot)) {
            setForecastSnapshot(cachedSnapshotForRequest)
            const localForecast = buildFromSnapshot(cachedSnapshotForRequest)
            if (localForecast) {
              setForecast(localForecast)
              setForecastCacheStatus('Using durable browser forecast package.')
              setLoading(false)
              return
            }
          }
          let warmSnapshot = snapshot
          if (!artifactBacked && !monthlyLineBacked) {
            snapshotCacheRef.current.delete(snapshotKey)
            clearForecastStudioSnapshotCache(requestDurableSnapshotKey)
            setForecast(null)
            setForecastSnapshot(snapshot)
            setForecastCacheStatus('')
            setError(grain === 'month'
              ? 'Forecast package is missing materialized artifacts and monthly forecast rows for the selected month.'
              : 'Daily and weekly cash forecasts require materialized Forecast ML artifacts for the selected month.')
            setLoading(false)
            return
          }
          // Release the prior package's scoped cache tables before warming a
          // different package, so OPFS handles do not pile up across switches.
          const nextWarmPackageId = trimStr(snapshot.package?.id)
          const priorWarmPackageId = warmedForecastPackageIdRef.current
          if (nextWarmPackageId && priorWarmPackageId && priorWarmPackageId !== nextWarmPackageId) {
            await releaseForecastPackageScopedTables(workerClient, priorWarmPackageId, duckDbOpts).catch(() => undefined)
            for (const warmKey of Array.from(warmedForecastArtifactKeysRef.current)) {
              if (warmKey.includes(priorWarmPackageId)) warmedForecastArtifactKeysRef.current.delete(warmKey)
            }
          }
          if (nextWarmPackageId) warmedForecastPackageIdRef.current = nextWarmPackageId
          setForecastSnapshot(snapshot)
          setForecastCacheStatus(monthlyLineBacked
            ? 'Rendering monthly forecast rows from package line metadata...'
            : grain === 'month'
              ? 'Warming monthly forecast artifacts in browser DuckDB...'
              : 'Warming daily cash forecast rows in browser DuckDB...')
          try {
            const foregroundStartedAt = Date.now()
            const useCashDailyRollup = artifactBacked && grain === 'month' && !monthlyLineBacked
            const foregroundArtifactKeys: ForecastArtifactKey[] = useCashDailyRollup
              ? ['cash_daily']
              : grain === 'month' || bridgeFromMonthly
                ? ['line_metadata', 'layout_node']
                : ['cash_daily']
            await warmForecastSnapshotInDuckDb(appApi, workerClient, wb, snapshot, duckDbOpts, {
              artifactKeys: foregroundArtifactKeys,
              timeoutMs: FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS,
              phase: useCashDailyRollup ? 'foreground_cash' : grain === 'month' || bridgeFromMonthly ? 'foreground_monthly_metadata' : 'foreground_cash',
              inFlight: forecastArtifactWarmInFlightRef.current,
              completed: warmedForecastArtifactKeysRef.current,
            })
            if (!sessionIsCurrent()) return
            if (!useCashDailyRollup && (grain === 'month' || bridgeFromMonthly)) {
              // Overlay user deltas + `_effective` views so line edits flow
              // into the monthly statements. The shared warm dedupes
              // already-warmed tables via cache keys. A failure here is a hard
              // failure surfaced through the page's error state — the monthly
              // rollup must never silently render without the user's edits.
              const warmContext = await warmForecastPackageForQueries(appApi, workerClient, wb, snapshot, duckDbOpts, ['line_metadata', 'layout_node'], {
                timeoutMs: FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS,
                phase: 'foreground_monthly_metadata',
                inFlight: forecastArtifactWarmInFlightRef.current,
                completed: warmedForecastArtifactKeysRef.current,
                scenarioKey: snapshot.scenario_key || snapshot.package?.active_scenario_key || null,
              })
              if (!sessionIsCurrent()) return
              setForecastDeltas(warmContext.deltas)
              setStaleDeltaKeys(warmContext.staleKeys)
              explainWarmedPackageRef.current = trimStr(snapshot.package?.id)
            }
            if (grain === 'month') {
              const monthlyQueryStartedAt = Date.now()
              const monthlyForecast = await loadDirectCashForecastFromDuckDb(
                workerClient,
                snapshot,
                grain,
                valueCase,
                selectedEndPeriod,
                futureMonths,
                duckDbOpts,
              )
              if (!sessionIsCurrent()) return
              forecastStudioPerf(useCashDailyRollup ? 'monthly_cash_daily_query_done' : 'monthly_line_metadata_query_done', {
                package_id: snapshot.package?.id || null,
                periods: monthlyForecast?.statement?.periods?.length || 0,
                rows: monthlyForecast?.statement?.rows?.length || 0,
                elapsed_ms: Date.now() - monthlyQueryStartedAt,
              })
              if (!monthlyForecast) {
                setForecast(null)
                setError(monthlyLineBacked
                  ? 'No monthly forecast rows could be rendered from the package line metadata.'
                  : 'No monthly forecast rows are available in the Forecast ML Parquet artifacts.')
              } else {
                forecastCacheRef.current.set(cacheKey, monthlyForecast)
                setForecast(monthlyForecast)
                setForecastCacheStatus(monthlyLineBacked
                  ? 'Monthly forecast rows rendered from package line metadata.'
                  : useCashDailyRollup
                    ? 'Monthly cash forecast rows rendered locally from cash artifacts.'
                    : 'Monthly forecast rows rendered locally from browser DuckDB.')
              }
              forecastStudioPerf('foreground_monthly_metadata_done', {
                package_id: snapshot.package?.id || null,
                elapsed_ms: Date.now() - foregroundStartedAt,
              })
            } else if (bridgeFromMonthly) {
              const bridgedQueryStartedAt = Date.now()
              const bridgedForecast = await loadDirectCashForecastFromDuckDb(
                workerClient,
                snapshot,
                grain,
                valueCase,
                selectedEndPeriod,
                snapshotMonths,
                duckDbOpts,
              )
              if (!sessionIsCurrent()) return
              forecastStudioPerf('cash_line_metadata_bridge_query_done', {
                package_id: snapshot.package?.id || null,
                periods: bridgedForecast?.statement?.periods?.length || 0,
                rows: bridgedForecast?.statement?.rows?.length || 0,
                elapsed_ms: Date.now() - bridgedQueryStartedAt,
              })
              if (!bridgedForecast) {
                setForecast(null)
                setError('No daily or weekly forecast rows could be allocated from the package line metadata.')
              } else {
                forecastCacheRef.current.set(cacheKey, bridgedForecast)
                setForecast(bridgedForecast)
                setForecastCacheStatus('Daily and weekly rows allocated from monthly package line metadata.')
              }
              forecastStudioPerf('foreground_monthly_metadata_done', {
                package_id: snapshot.package?.id || null,
                elapsed_ms: Date.now() - foregroundStartedAt,
              })
            } else {
              const cashQueryStartedAt = Date.now()
              const cashDaily = await loadForecastCashDailyFromDuckDb(workerClient, snapshot, duckDbOpts)
              if (!sessionIsCurrent()) return
              forecastStudioPerf('cash_daily_query_done', {
                package_id: snapshot.package?.id || null,
                rows: cashDaily.length,
                elapsed_ms: Date.now() - cashQueryStartedAt,
              })
              warmSnapshot = { ...snapshot, cash_daily: cashDaily }
              setForecastCacheStatus('Daily cash forecast rows are warm in browser DuckDB.')
              forecastStudioPerf('foreground_cash_done', {
                package_id: snapshot.package?.id || null,
                rows: cashDaily.length,
                elapsed_ms: Date.now() - foregroundStartedAt,
              })
            }
            if (artifactBacked) {
              void warmForecastSnapshotInDuckDb(appApi, workerClient, wb, snapshot, duckDbOpts, {
                artifactKeys: grain === 'month'
                  ? []
                  : ['line_metadata', 'layout_node'],
                timeoutMs: FORECAST_STUDIO_BACKGROUND_ARTIFACT_TIMEOUT_MS,
                phase: 'background_metadata',
                inFlight: forecastArtifactWarmInFlightRef.current,
                completed: warmedForecastArtifactKeysRef.current,
              }).catch((err) => {
                forecastStudioPerf('background_metadata_failed', {
                  package_id: snapshot.package?.id || null,
                  message: trimStr((err as any)?.message || err),
                })
              })
            }
          } catch (err: any) {
            forecastStudioPerf('foreground_cash_failed', {
              package_id: snapshot.package?.id || null,
              message: trimStr(err?.message || err),
            })
            if (isDuckDbAccessHandleConflict(err)) {
              // The warm path already released handles and retried once; a
              // persistent conflict is a local-cache problem, not a package
              // problem, so keep it out of the red API error banner.
              setLocalCacheWarning('Browser local forecast cache is unavailable in this tab (another FuseGrid tab may hold it). Close other FuseGrid tabs or reload to re-enable cached forecasts.')
              setForecastCacheStatus('')
              setLoading(false)
              return
            }
            throw err
          }
          snapshotCacheRef.current.set(snapshotKey, warmSnapshot)
          if (artifactBacked) {
            saveForecastStudioSnapshotCache(requestDurableSnapshotKey, warmSnapshot)
          } else {
            clearForecastStudioSnapshotCache(requestDurableSnapshotKey)
          }
          setForecastSnapshot(warmSnapshot)
          if (grain === 'month') return
          if (bridgeFromMonthly) return
          const localForecast = buildFromSnapshot(warmSnapshot)
          if (localForecast) {
            setForecast(localForecast)
          } else {
            setForecast(null)
            setError('No daily cash forecast rows are available for the selected anchor.')
          }
        })
        .catch((err: any) => {
          if (!sessionIsCurrent()) return
          forecastStudioPerf('snapshot_request_failed', {
            workbook_id: wb,
            message: trimStr(err?.message || err),
            elapsed_ms: Date.now() - requestStartedAt,
          })
          snapshotCacheRef.current.delete(snapshotKey)
          clearForecastStudioSnapshotCache(requestDurableSnapshotKey)
          setForecast(null)
          setForecastSnapshot(null)
          setForecastCacheStatus('')
          setError(trimStr(err?.message || err) || 'Could not load Forecast Studio.')
        })
        .finally(() => {
          if (sessionIsCurrent()) setLoading(false)
        })
      }
      startForecastStudioLoad(runSnapshotLoad)
      return () => {
        cancelled = true
      }
    }
    const statementSnapshotMonths = Math.max(3, futureMonths)
    const statementSnapshotMode = forecastStudioSnapshotMode('month')
    const runStatementForecastLoad = (ensuredPackageId: string | null) => {
      const requestPackageId = trimStr(ensuredPackageId) || null
      const statementSnapshotKey = JSON.stringify({ wb, selectedEndPeriod, futureMonths: statementSnapshotMonths, snapshotMode: statementSnapshotMode, scenarioKey, packageId: requestPackageId })
      const durableStatementSnapshotKey = forecastStudioSnapshotCacheKey(wb, selectedEndPeriod, statementSnapshotMonths, statementSnapshotMode, scenarioKey, requestPackageId)
      let snapshotPromise = snapshotRequestInFlightRef.current.get(statementSnapshotKey)
      if (!snapshotPromise) {
        snapshotPromise = appApi.getQuickBooksFpaMlForecastPackageSnapshot(wb, {
          package_id: requestPackageId || undefined,
          scenario_key: scenarioKey === 'base' ? undefined : scenarioKey,
          as_of_date: selectedEndPeriod,
          grain: 'month',
          future_months: statementSnapshotMonths,
          daily_limit: 1,
        }, { timeoutMs: FORECAST_STUDIO_SNAPSHOT_TIMEOUT_MS })
        snapshotRequestInFlightRef.current.set(statementSnapshotKey, snapshotPromise)
        void snapshotPromise
          .finally(() => {
            if (snapshotRequestInFlightRef.current.get(statementSnapshotKey) === snapshotPromise) {
              snapshotRequestInFlightRef.current.delete(statementSnapshotKey)
            }
          })
          .catch(() => undefined)
      }
      snapshotPromise
        .then(async (snapshot) => {
          if (!sessionIsCurrent()) return
          if (!forecastSnapshotMatchesAnchor(snapshot, selectedEndPeriod)) {
            throw new Error('Forecast package does not match the selected anchor month.')
          }
          if (!snapshotHasParquetArtifacts(snapshot)) {
            throw new Error('Forecast package is missing materialized artifacts for local statement rendering.')
          }
          const nextWarmPackageId = trimStr(snapshot.package?.id)
          const priorWarmPackageId = warmedForecastPackageIdRef.current
          if (nextWarmPackageId && priorWarmPackageId && priorWarmPackageId !== nextWarmPackageId) {
            await releaseForecastPackageScopedTables(workerClient, priorWarmPackageId, duckDbOpts).catch(() => undefined)
            for (const warmKey of Array.from(warmedForecastArtifactKeysRef.current)) {
              if (warmKey.includes(priorWarmPackageId)) warmedForecastArtifactKeysRef.current.delete(warmKey)
            }
          }
          if (nextWarmPackageId) warmedForecastPackageIdRef.current = nextWarmPackageId
          setForecastSnapshot(snapshot)
          setForecastCacheStatus('Rendering statement from browser forecast artifacts...')
          const warmContext = await warmForecastPackageForQueries(appApi, workerClient, wb, snapshot, duckDbOpts, ['line_metadata', 'layout_node'], {
            timeoutMs: FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS,
            phase: 'foreground_statement_metadata',
            inFlight: forecastArtifactWarmInFlightRef.current,
            completed: warmedForecastArtifactKeysRef.current,
            scenarioKey: snapshot.scenario_key || snapshot.package?.active_scenario_key || null,
          })
          if (!sessionIsCurrent()) return
          setForecastDeltas(warmContext.deltas)
          setStaleDeltaKeys(warmContext.staleKeys)
          explainWarmedPackageRef.current = trimStr(snapshot.package?.id)
          const forecastOut = await forecastStatementFromDuckDb(
            workerClient,
            snapshot,
            statementKind,
            valueCase,
            selectedEndPeriod,
            futureMonths,
            duckDbOpts,
          )
          if (!sessionIsCurrent()) return
          if (!forecastOut) throw new Error('No forecast statement rows are available in the package artifacts.')
          forecastCacheRef.current.set(cacheKey, forecastOut)
          snapshotCacheRef.current.set(statementSnapshotKey, snapshot)
          saveForecastStudioSnapshotCache(durableStatementSnapshotKey, snapshot)
          setForecastSnapshot(snapshot)
          setForecast(forecastOut)
          setForecastCacheStatus('Statement rendered locally from browser DuckDB.')
        })
        .catch((err: any) => {
          if (!sessionIsCurrent()) return
          snapshotCacheRef.current.delete(statementSnapshotKey)
          clearForecastStudioSnapshotCache(durableStatementSnapshotKey)
          setForecast(null)
          setError(trimStr(err?.message || err) || 'Could not load Forecast Studio.')
        })
        .finally(() => {
          if (sessionIsCurrent()) setLoading(false)
        })
    }
    startForecastStudioLoad(runStatementForecastLoad)
    return () => {
      cancelled = true
    }
  }, [appApi, sessionIdentity, bootstrapLoading, duckDbOpts, ensureRetryNonce, futureMonths, grain, nativeForecastWorkbook, scenarioKey, selectedEndPeriod, serverForecastFirstPaint, statementKind, valueCase, workbookId, workerClient])

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!forecast || currentSource) {
      setInsights(null); setComparisons(null); setForecastVsActual(null)
      return
    }
    if (!wb || (!selectedEndPeriod && bootstrapLoading)) return
    if (selectedEndPeriod && forecastPackageId && !selectedForecastPackageId) {
      setAssumptions(null)
      return
    }
    const assumptionReq = selectedForecastPackageId ? { package_id: selectedForecastPackageId } : undefined
    const loadAnalyticsSidecars = shouldLoadForecastAnalyticsSidecars(bootstrapLoading, hasBootstrap, cfoAnalyticsReady)
    Promise.all([
      isQuickBooksProvider
        ? appApi.getQuickBooksFpaMlDriverAssumptions(wb, assumptionReq).catch(() => null)
        : Promise.resolve(null),
      loadAnalyticsSidecars
        ? appApi.getCfoInsights(wb, { asOf: selectedEndPeriod, grain: 'month' }).catch(() => null)
        : Promise.resolve(null),
      loadAnalyticsSidecars
        ? appApi.getCfoMetricComparisons(wb, {
          asOf: selectedEndPeriod,
          grain: 'month',
          metrics: ['revenue', 'net_income', 'cash', 'ending_cash', 'gross_margin', 'dso', 'days_sales_outstanding', 'dpo', 'days_payable_outstanding', 'operating_expenses'],
          comparisons: FORECAST_REQUIRED_COMPARISONS,
        }).catch(() => null)
        : Promise.resolve(null),
      loadAnalyticsSidecars && isQuickBooksProvider
        ? appApi.getQuickBooksFpaMlForecastVsActual(wb, selectedForecastPackageId || undefined).catch((err: any) => {
          forecastStudioPerf('forecast_accuracy_load_failed', { message: trimStr(err?.message || err) })
          return null
        })
        : Promise.resolve(null),
    ])
      .then(([assumptionOut, insightOut, comparisonOut, forecastVsActualOut]) => {
        if (cancelled || accessSessionIdentity(appApi.getToken?.()) !== sessionIdentity) return
        setAssumptions(assumptionOut)
        setInsights(insightOut)
        setComparisons(comparisonOut)
        setForecastVsActual(forecastVsActualOut)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, sessionIdentity, forecast, currentSource, bootstrapLoading, cfoAnalyticsReady, comparisonKind, forecastPackageId, hasBootstrap, isQuickBooksProvider, selectedEndPeriod, selectedForecastPackageId, workbookId])

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    const snapshot = forecastSnapshot
    const snapshotMonths = grain === 'month' ? futureMonths : Math.max(3, futureMonths)
    const expectedForecastReady = Boolean(
      forecast?.statement_type === 'cash_flow'
      && forecast?.value_case === valueCase
      && forecast?.statement?.rows?.length
      && forecast.validation?.status !== 'warming',
    )
    const key = JSON.stringify({
      wb,
      package_id: snapshot?.package?.id || null,
      package_updated_at: snapshot?.package?.updated_at || null,
      scenario: trimStr(snapshot?.scenario_key) || 'base',
      grain,
      selectedEndPeriod,
      futureMonths: snapshotMonths,
    })
    if (!wb || statementKind !== 'cash_flow' || !expectedForecastReady || !snapshot?.package || !snapshotHasParquetArtifacts(snapshot)) {
      setForecastBandStatements(null)
      return
    }
    setForecastBandStatements((current) => (current?.key === key ? current : null))
    const loadBands = async () => {
      const startedAt = performance.now()
      let low: QuickBooksFpaMlStatementForecastResponse | null = null
      let high: QuickBooksFpaMlStatementForecastResponse | null = null
      if (workerClient) {
        const monthlyLineBacked = snapshotSupportsMonthlyLineMetadata(snapshot)
        if (grain === 'month' && monthlyLineBacked) {
          await warmForecastPackageForQueries(appApi, workerClient, wb, snapshot, duckDbOpts, ['line_metadata', 'layout_node'], {
            timeoutMs: FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS,
            phase: 'trend_band_monthly_metadata',
            inFlight: forecastArtifactWarmInFlightRef.current,
            completed: warmedForecastArtifactKeysRef.current,
            scenarioKey: snapshot.scenario_key || snapshot.package?.active_scenario_key || null,
          })
        } else {
          await warmForecastSnapshotInDuckDb(appApi, workerClient, wb, snapshot, duckDbOpts, {
            artifactKeys: ['cash_daily'],
            timeoutMs: FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS,
            phase: 'trend_band_cash',
            inFlight: forecastArtifactWarmInFlightRef.current,
            completed: warmedForecastArtifactKeysRef.current,
          })
        }
        ;[low, high] = await Promise.all([
          loadDirectCashForecastFromDuckDb(workerClient, snapshot, grain, 'low', selectedEndPeriod, snapshotMonths, duckDbOpts),
          loadDirectCashForecastFromDuckDb(workerClient, snapshot, grain, 'high', selectedEndPeriod, snapshotMonths, duckDbOpts),
        ])
      } else {
        low = buildDirectCashForecastFromSnapshot(snapshot, grain, 'low', selectedEndPeriod, snapshotMonths)
        high = buildDirectCashForecastFromSnapshot(snapshot, grain, 'high', selectedEndPeriod, snapshotMonths)
      }
      if (cancelled) return
      setForecastBandStatements({
        key,
        low: low?.statement || null,
        high: high?.statement || null,
      })
      forecastStudioPerf('trend_band_load_done', {
        workbook_id: wb,
        package_id: snapshot.package?.id || null,
        grain,
        elapsed_ms: Math.round(performance.now() - startedAt),
      })
    }
    void loadBands().catch((err) => {
      forecastStudioPerf('trend_band_load_failed', {
        workbook_id: wb,
        package_id: snapshot.package?.id || null,
        message: trimStr((err as any)?.message || err),
      })
      if (!cancelled) setForecastBandStatements(null)
    })
    return () => {
      cancelled = true
    }
  }, [appApi, duckDbOpts, forecast, forecastSnapshot, futureMonths, grain, selectedEndPeriod, statementKind, valueCase, workbookId, workerClient])

  // Base-scenario comparison series for non-base scenarios. The package's
  // parquet artifacts hold every scenario's rows, so a scenario-overridden
  // copy of the warmed snapshot re-queries the same DuckDB tables with
  // scenario_key = 'base' — no extra warm, no extra network.
  useEffect(() => {
    let cancelled = false
    const snapshot = forecastSnapshot
    const activeScenario = trimStr(snapshot?.scenario_key) || 'base'
    if (currentSource) { setScenarioBaseForecast(null); return }
    if (
      !snapshot?.package
      || activeScenario === 'base'
      || !workerClient
      || !snapshotHasParquetArtifacts(snapshot)
      || !forecast?.statement?.rows?.length
    ) {
      setScenarioBaseForecast(null)
      return
    }
    const baseSnapshot = { ...snapshot, scenario_key: 'base' }
    const load = statementKind === 'cash_flow'
      ? loadDirectCashForecastFromDuckDb(workerClient, baseSnapshot, grain, valueCase, selectedEndPeriod, grain === 'month' ? futureMonths : Math.max(3, futureMonths), duckDbOpts)
      : forecastStatementFromDuckDb(workerClient, baseSnapshot, statementKind, valueCase, selectedEndPeriod, futureMonths, duckDbOpts)
    load
      .then((out) => {
        if (!cancelled) setScenarioBaseForecast(out)
      })
      .catch((err: any) => {
        forecastStudioPerf('scenario_base_compare_load_failed', {
          package_id: snapshot.package?.id || null,
          scenario_key: activeScenario,
          message: trimStr(err?.message || err),
        })
        if (!cancelled) setScenarioBaseForecast(null)
      })
    return () => {
      cancelled = true
    }
  }, [duckDbOpts, forecast, forecastSnapshot, futureMonths, grain, selectedEndPeriod, statementKind, valueCase, workerClient])

  useEffect(() => {
    let cancelled = false
    const target = explainTarget
    const snapshot = forecastSnapshot
    setExplainReviewStatus('')
    if (!target || !snapshot?.package) {
      setExplainLine(null)
      setExplainLoading(false)
      setExplainLimited(false)
      setExplainError('')
      return
    }
    const legacyLine = (snapshot.line_metadata || []).find((line) =>
      trimStr(line.node_id) === target.nodeId
      && String(line.period_start || '').slice(0, 10) === target.period
      && (trimStr(line.scenario_key) || 'base') === forecastScenarioKey
      && (trimStr(line.period_grain) || 'month') === 'month') || null
    if (!snapshotHasParquetArtifacts(snapshot) || !workerClient) {
      // Legacy (non-artifact) packages only carry the snapshot's line rows;
      // the drawer labels this explicitly instead of pretending it is the
      // deltas-overlaid artifact row.
      setExplainLine(legacyLine)
      setExplainLoading(false)
      setExplainLimited(true)
      setExplainError('')
      return
    }
    setExplainLoading(true)
    setExplainLimited(false)
    setExplainError('')
    void (async () => {
      try {
        // The cash-daily monthly rollup never warms line metadata, so the
        // drawer warms it (and the deltas overlay) on first use; repeat opens
        // are deduped per package.
        const packageId = trimStr(snapshot.package?.id)
        if (explainWarmedPackageRef.current !== packageId) {
          const warmContext = await warmForecastPackageForQueries(appApi, workerClient, trimStr(workbookId), snapshot, duckDbOpts, ['line_metadata', 'layout_node'], {
            timeoutMs: FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS,
            phase: 'explain_drawer',
            inFlight: forecastArtifactWarmInFlightRef.current,
            completed: warmedForecastArtifactKeysRef.current,
            scenarioKey: forecastScenarioKey,
          })
          explainWarmedPackageRef.current = packageId
          if (cancelled) return
          setForecastDeltas(warmContext.deltas)
          setStaleDeltaKeys(warmContext.staleKeys)
        }
        const line = await queryForecastLineExplain(workerClient, snapshot, forecastScenarioKey, duckDbOpts, {
          nodeId: target.nodeId,
          periodStart: target.period,
          statementType: target.statementType || null,
        })
        if (cancelled) return
        setExplainLine(line)
      } catch (err: any) {
        if (cancelled) return
        setExplainLine(null)
        setExplainError(trimStr(err?.message || err) || 'Could not load line detail.')
      } finally {
        if (!cancelled) setExplainLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [appApi, duckDbOpts, explainTarget, forecastScenarioKey, forecastSnapshot, workbookId, workerClient])

  // Fetch (and dedupe) the governed CFO statement render contract for a workbook
  // at an as-of month. Returns null when the contract route is unavailable so the
  // caller falls back to the raw FPA render. The window is month-grain only and
  // covers the last ~24 months, which fully spans the forecast's visible actual
  // history, so the displayed/bridged actuals tie out to the Financials page.
  const loadGovernedStatements = useCallback(
    (wb: string, asOf: string): Promise<QuickBooksCfoStatementsRenderContractResponse> | null => {
      if (!wb || !asOf) return null
      if (typeof appApi.getCfoStatementsRender !== 'function') return null
      const key = `${wb}|${asOf}`
      const existing = governedStatementsRef.current.get(key)
      if (existing) return existing
      const promise = appApi
        .getCfoStatementsRender(wb, { asOf, grain: 'month' })
        .catch((err: any) => {
          // Drop the cached rejection so a later attempt can retry the contract
          // before settling for the fallback render.
          governedStatementsRef.current.delete(key)
          throw err
        })
      governedStatementsRef.current.set(key, promise)
      return promise
    },
    [appApi],
  )

  useEffect(() => {
    let cancelled = false
    if (nativeReportingAnchor) {
      setActualStatement(forecast?.current_actual_statement || null); setActualLoading(false); setActualStatementError('')
      return
    }
    const wb = trimStr(workbookId)
    const connectionId = actualConnectionId
    const layoutVersionId = actualLayoutVersionId
    const anchorPeriod = actualAnchorPeriod
    if (statementKind === 'cash_flow' && isDirectCashForecast(forecast?.statement)) {
      setActualStatement(null)
      setActualLoading(false)
      setActualStatementError('')
      return
    }
    if (!wb || !connectionId || !anchorPeriod) {
      setActualStatement(null)
      setActualLoading(false)
      setActualStatementError('')
      return
    }
    const layoutVersionCacheKey = layoutVersionId || 'latest_published'
    const historyStartPeriod = modelHistoryStartPeriod
    const cacheKey = JSON.stringify({ wb, connectionId, layoutVersionId: layoutVersionCacheKey, statementKind, anchorPeriod, historyStartPeriod })
    const cached = actualCacheRef.current.get(cacheKey)
    if (cached) {
      setActualStatement(cached)
      setActualLoading(false)
      setActualStatementError('')
      return
    }
    setActualLoading(true)
    setActualStatementError('')
    const actualStartedAt = Date.now()
    forecastStudioPerf('actual_history_request_start', {
      workbook_id: wb,
      statement_kind: statementKind,
      layout_version_id: layoutVersionId || 'latest_published',
      start_date: historyStartPeriod,
      end_date: anchorPeriod,
    })
    const renderActualHistory = () => appApi.renderQuickBooksFpaStatement({
      connection_id: connectionId,
      layout_version_id: layoutVersionId || null,
      workbook_id: wb,
      statement_type: statementKind,
      start_date: historyStartPeriod,
      end_date: anchorPeriod,
      balance_sheet_mode: statementKind === 'balance_sheet' ? 'ending_balance' : undefined,
    })
    // Prefer the governed render contract (same source as the Financials page) at
    // month grain so the forecast's historical actuals match the statement detail
    // exactly; fall back to the raw FPA render otherwise.
    const governedHistory = grain === 'month' ? loadGovernedStatements(wb, anchorPeriod) : null
    const actualHistoryPromise = governedHistory
      ? governedHistory.then((contract) => governedStatementFromContract(contract, statementKind)).catch(renderActualHistory)
      : renderActualHistory()
    actualHistoryPromise
      .then((rendered) => {
        if (cancelled) return
        actualCacheRef.current.set(cacheKey, rendered)
        setActualStatement(rendered)
        setActualStatementError('')
        forecastStudioPerf('actual_history_request_done', {
          workbook_id: wb,
          statement_kind: statementKind,
          periods: rendered.periods?.length || 0,
          rows: rendered.rows?.length || 0,
          elapsed_ms: Date.now() - actualStartedAt,
        })
      })
      .catch((err: any) => {
        forecastStudioPerf('actual_history_request_failed', {
          workbook_id: wb,
          statement_kind: statementKind,
          message: trimStr(err?.message || err),
          elapsed_ms: Date.now() - actualStartedAt,
        })
        if (!cancelled) {
          setActualStatement(null)
          setActualStatementError(`Actual ${statementLabel(statementKind)} history could not load: ${trimStr(err?.message || err) || 'render request failed.'}`)
        }
      })
      .finally(() => {
        if (!cancelled) setActualLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [actualAnchorPeriod, actualConnectionId, actualLayoutVersionId, appApi, forecast?.current_actual_statement, forecast?.statement, grain, loadGovernedStatements, modelHistoryStartPeriod, nativeReportingAnchor, statementKind, workbookId])

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    const connectionId = actualConnectionId
    const layoutVersionId = actualLayoutVersionId
    const anchorPeriod = actualAnchorPeriod
    const needsBasis = !nativeReportingAnchor && statementKind === 'cash_flow' && isDirectCashForecast(forecast?.statement)
    if (!needsBasis || !wb || !connectionId || !anchorPeriod) {
      setActualBasisLoading(false)
      setActualBasisStatement(null)
      setActualBalanceSheetStatement(null)
      setActualBasisError('')
      return
    }
    const layoutVersionCacheKey = layoutVersionId || 'latest_published'
    const historyStartPeriod = modelHistoryStartPeriod
    const balanceSheetHistoryStartPeriod = modelBalanceSheetHistoryStartPeriod || historyStartPeriod
    const pnlCacheKey = JSON.stringify({ wb, connectionId, layoutVersionId: layoutVersionCacheKey, statementKind: 'profit_and_loss', anchorPeriod, historyStartPeriod })
    const bsCacheKey = JSON.stringify({ wb, connectionId, layoutVersionId: layoutVersionCacheKey, statementKind: 'balance_sheet', anchorPeriod, historyStartPeriod: balanceSheetHistoryStartPeriod, balance_sheet_mode: 'ending_balance' })
    const cachedPnl = actualCacheRef.current.get(pnlCacheKey)
    const cachedBs = actualCacheRef.current.get(bsCacheKey)
    if (cachedPnl) setActualBasisStatement(cachedPnl)
    if (cachedBs) setActualBalanceSheetStatement(cachedBs)
    if (cachedPnl && cachedBs) {
      setActualBasisLoading(false)
      setActualBasisError('')
      return
    }
    setActualBasisLoading(true)
    setActualBasisError('')
    const basisStartedAt = Date.now()
    forecastStudioPerf('actual_cash_basis_request_start', {
      workbook_id: wb,
      layout_version_id: layoutVersionId || 'latest_published',
      start_date: historyStartPeriod,
      end_date: anchorPeriod,
      needs_pnl: !cachedPnl,
      needs_balance_sheet: !cachedBs,
    })
    // Source the cash-bridge P&L and balance-sheet basis from the same governed
    // render contract the Financials page uses (month grain) so the bridge is
    // built on the identical canonical actuals; fall back to the raw FPA render
    // per-statement if the contract route is unavailable or rejects.
    const governedBasis = grain === 'month' ? loadGovernedStatements(wb, anchorPeriod) : null
    const renderPnlBasis = () => appApi.renderQuickBooksFpaStatement({
      connection_id: connectionId,
      layout_version_id: layoutVersionId || null,
      workbook_id: wb,
      statement_type: 'profit_and_loss',
      start_date: historyStartPeriod,
      end_date: anchorPeriod,
    })
    const renderBsBasis = () => appApi.renderQuickBooksFpaStatement({
      connection_id: connectionId,
      layout_version_id: layoutVersionId || null,
      workbook_id: wb,
      statement_type: 'balance_sheet',
      start_date: balanceSheetHistoryStartPeriod,
      end_date: anchorPeriod,
      balance_sheet_mode: 'ending_balance',
    })
    const pnlPromise = cachedPnl
      ? Promise.resolve(cachedPnl)
      : governedBasis
        ? governedBasis.then((contract) => contract.profit_and_loss).catch(renderPnlBasis)
        : renderPnlBasis()
    const bsPromise = cachedBs
      ? Promise.resolve(cachedBs)
      : governedBasis
        ? governedBasis.then((contract) => contract.balance_sheet).catch(renderBsBasis)
        : renderBsBasis()
    Promise.allSettled([pnlPromise, bsPromise])
      .then(([pnlResult, bsResult]) => {
        if (cancelled) return
        if (pnlResult.status === 'fulfilled') {
          actualCacheRef.current.set(pnlCacheKey, pnlResult.value)
          setActualBasisStatement(pnlResult.value)
        } else {
          setActualBasisStatement(null)
        }
        if (bsResult.status === 'fulfilled') {
          actualCacheRef.current.set(bsCacheKey, bsResult.value)
          setActualBalanceSheetStatement(bsResult.value)
        } else {
          setActualBalanceSheetStatement(null)
        }
        const failures = [
          pnlResult.status === 'rejected' ? `P&L: ${trimStr((pnlResult.reason as any)?.message || pnlResult.reason) || 'render request failed'}` : '',
          bsResult.status === 'rejected' ? `Balance sheet: ${trimStr((bsResult.reason as any)?.message || bsResult.reason) || 'render request failed'}` : '',
        ].filter(Boolean)
        setActualBasisError(failures.length ? `Actual drilldown source could not load. ${failures.join('; ')}` : '')
        forecastStudioPerf('actual_cash_basis_request_done', {
          workbook_id: wb,
          pnl_status: pnlResult.status,
          balance_sheet_status: bsResult.status,
          pnl_rows: pnlResult.status === 'fulfilled' ? pnlResult.value.rows?.length || 0 : 0,
          balance_sheet_rows: bsResult.status === 'fulfilled' ? bsResult.value.rows?.length || 0 : 0,
          pnl_error: pnlResult.status === 'rejected' ? trimStr((pnlResult.reason as any)?.message || pnlResult.reason) : null,
          balance_sheet_error: bsResult.status === 'rejected' ? trimStr((bsResult.reason as any)?.message || bsResult.reason) : null,
          elapsed_ms: Date.now() - basisStartedAt,
        })
      })
      .finally(() => {
        if (!cancelled) setActualBasisLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [actualAnchorPeriod, actualConnectionId, actualLayoutVersionId, appApi, forecast?.statement, grain, loadGovernedStatements, modelBalanceSheetHistoryStartPeriod, modelHistoryStartPeriod, nativeReportingAnchor, statementKind, workbookId])

  const cashBalanceAtActualAnchor = balanceSheetCashValues(actualBalanceSheetStatement, 1)[0]
    ?? statementLineValue(actualStatement, /^ending cash$/i)
    ?? metricValue(comparisons, ['cash', 'ending_cash'])

  useEffect(() => {
    let cancelled = false
    const connectionId = actualConnectionId
    const layoutVersionId = actualLayoutVersionId
    const anchorPeriod = actualAnchorPeriod
    const historyStartPeriod = forecastStudioVisibleHistoryStartPeriod(anchorPeriod, actualHistoryPeriods, grain)
    const needsDetailCash = !nativeReportingAnchor && statementKind === 'cash_flow'
      && (grain === 'weekly' || grain === 'daily')
      && isDirectCashForecast(forecast?.statement)
    if (!needsDetailCash) {
      setActualDetailBasisStatement(null)
      setActualDetailBalanceSheetStatement(null)
      setActualDetailCashError('')
      return
    }
    if (!connectionId || !anchorPeriod || !historyStartPeriod || !actualBasisStatement?.rows?.length) {
      setActualDetailBasisStatement(null)
      setActualDetailBalanceSheetStatement(null)
      setActualDetailCashError('')
      return
    }
    const cacheKey = JSON.stringify({
      connectionId,
      layoutVersionId: layoutVersionId || 'latest_published',
      grain,
      historyStartPeriod,
      anchorPeriod,
      actualBasisSignature: `${actualBasisStatement.periods?.[0] || ''}|${actualBasisStatement.periods?.[actualBasisStatement.periods.length - 1] || ''}|${actualBasisStatement.rows?.length || 0}`,
      actualBalanceSheetSignature: `${actualBalanceSheetStatement?.periods?.[0] || ''}|${actualBalanceSheetStatement?.periods?.[actualBalanceSheetStatement.periods.length - 1] || ''}|${actualBalanceSheetStatement?.rows?.length || 0}`,
    })
    const balanceSheetCacheKey = `${cacheKey}:balance_sheet`
    const cachedBasis = actualCacheRef.current.get(cacheKey) as (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | undefined
    const cachedBalanceSheet = actualCacheRef.current.get(balanceSheetCacheKey) as (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | undefined
    if (cachedBasis) {
      setActualDetailBasisStatement(cachedBasis)
      setActualDetailBalanceSheetStatement(cachedBalanceSheet || null)
      setActualDetailCashError('')
      return
    }
    const startedAt = Date.now()
    let request = actualDetailBasisInFlightRef.current.get(cacheKey)
    if (request) {
      forecastStudioPerf('actual_detail_cash_request_joined', {
        connection_id: connectionId,
        grain,
        start_date: historyStartPeriod,
        end_date: anchorPeriod,
      })
    } else {
      setActualLoading(true)
      setActualDetailCashError('')
      forecastStudioPerf('actual_detail_cash_request_start', {
        connection_id: connectionId,
        grain,
        start_date: historyStartPeriod,
        end_date: anchorPeriod,
      })
      request = loadDirectCashActualDetailStatementsFromGlDetail(
        appApi,
        workerClient,
        connectionId,
        layoutVersionId,
        actualBasisStatement,
        actualBalanceSheetStatement,
        grain,
        historyStartPeriod,
        anchorPeriod,
        duckDbOpts,
      ).finally(() => {
        if (actualDetailBasisInFlightRef.current.get(cacheKey) === request) {
          actualDetailBasisInFlightRef.current.delete(cacheKey)
        }
      })
      actualDetailBasisInFlightRef.current.set(cacheKey, request)
    }
    request
      .then((statements) => {
        if (cancelled) return
        if (statements.actualBasisStatement) actualCacheRef.current.set(cacheKey, statements.actualBasisStatement)
        if (statements.actualBalanceSheetStatement) actualCacheRef.current.set(balanceSheetCacheKey, statements.actualBalanceSheetStatement)
        setActualDetailBasisStatement(statements.actualBasisStatement)
        setActualDetailBalanceSheetStatement(statements.actualBalanceSheetStatement)
        setActualDetailCashError('')
        forecastStudioPerf('actual_detail_cash_request_done', {
          connection_id: connectionId,
          grain,
          periods: statements.actualBasisStatement?.periods?.length || 0,
          rows: statements.actualBasisStatement?.rows?.length || 0,
          balance_sheet_rows: statements.actualBalanceSheetStatement?.rows?.length || 0,
          elapsed_ms: Date.now() - startedAt,
        })
      })
      .catch((err: any) => {
        forecastStudioPerf('actual_detail_cash_request_failed', {
          connection_id: connectionId,
          grain,
          message: trimStr(err?.message || err),
          elapsed_ms: Date.now() - startedAt,
        })
        if (!cancelled) {
          setActualDetailBasisStatement(null)
          setActualDetailBalanceSheetStatement(null)
          setActualDetailCashError(`Actual ${grain === 'daily' ? 'daily' : 'weekly'} cash flow history could not load: ${trimStr(err?.message || err) || 'GL detail is not ready.'}`)
        }
      })
      .finally(() => {
        if (!cancelled) setActualLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [actualAnchorPeriod, actualBalanceSheetStatement, actualBasisStatement, actualConnectionId, actualHistoryPeriods, actualLayoutVersionId, appApi, duckDbOpts, forecast?.statement, grain, nativeReportingAnchor, statementKind, workerClient])

  // Pacing — monthly: fetch the current in-progress month's forecast (P&L + cash) and its
  // month-to-date actuals (P&L render + balance-sheet cash). Decoupled from the on-screen
  // statement so it works regardless of the selected statement type.
  useEffect(() => {
    if (nativeReportingAnchor) {
      setPacingForecastPnl(null); setPacingForecastCash(null); setPacingActualPnl(null)
      setPacingActualCashBalance(null); setPacingLoading(false); setPacingError(''); setPacingAsOf('')
      return undefined
    }
    if (grain !== 'month') return undefined
    const wb = workbookId
    const connectionId = actualConnectionId
    const today = new Date().toISOString().slice(0, 10)
    const currentMonth = monthStartIso(today)
    // Edge: the current month is already the most recent closed month → no in-progress forecast.
    if (!proFeatures || !wb || !connectionId || !forecastPackageId
      || (actualAnchorPeriod && monthStartIso(actualAnchorPeriod) === currentMonth)) {
      setPacingForecastPnl(null)
      setPacingForecastCash(null)
      setPacingActualPnl(null)
      setPacingActualCashBalance(null)
      return undefined
    }
    let cancelled = false
    const pkg = selectedForecastPackageId || forecastPackageId
    setPacingLoading(true)
    setPacingError('')
    const governedActuals = appApi.getCfoStatementsRender(wb, { asOf: currentMonth, grain: 'month' })
    Promise.all([
      getStatementForecastCompatible(wb, { package_id: pkg || null, scenario_key: forecastScenarioKey, grain: 'month', statement_type: 'profit_and_loss', future_months: 3 }),
      getStatementForecastCompatible(wb, { package_id: pkg || null, scenario_key: forecastScenarioKey, grain: 'month', statement_type: 'cash_flow', future_months: 3 }),
      governedActuals.then((contract) => contract.profit_and_loss),
      governedActuals.then((contract) => contract.balance_sheet),
    ])
      .then(([forecastPnl, forecastCash, actualPnl, actualBs]) => {
        if (cancelled) return
        setPacingForecastPnl(forecastPnl?.statement || null)
        setPacingForecastCash(forecastCash?.statement || null)
        setPacingActualPnl(actualPnl || null)
        const cash = balanceSheetCashValues(actualBs, 1)[0]
        setPacingActualCashBalance(cash == null ? null : cash)
        setPacingAsOf(currentMonth)
      })
      .catch((err: any) => {
        if (cancelled) return
        setPacingError(`Pacing could not load: ${trimStr(err?.message || err) || 'request failed.'}`)
        setPacingForecastPnl(null)
        setPacingForecastCash(null)
        setPacingActualPnl(null)
        setPacingActualCashBalance(null)
      })
      .finally(() => {
        if (!cancelled) setPacingLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [nativeReportingAnchor, grain, workbookId, actualConnectionId, actualLayoutVersionId, actualAnchorPeriod, forecastPackageId, selectedForecastPackageId, forecastScenarioKey, appApi, proFeatures])

  // Pacing — weekly/daily: cash planning already carries forecast + actual per week.
  useEffect(() => {
    if (nativeReportingAnchor) {
      setPacingCashWeeks(null); setPacingLoading(false); setPacingError(''); setPacingAsOf('')
      return undefined
    }
    if (grain !== 'weekly' && grain !== 'daily') return undefined
    const wb = workbookId
    if (!proFeatures || !wb || typeof appApi.getCfoCashPlanning !== 'function') {
      setPacingCashWeeks(null)
      return undefined
    }
    let cancelled = false
    const today = new Date().toISOString().slice(0, 10)
    setPacingLoading(true)
    setPacingError('')
    appApi.getCfoCashPlanning(wb, { scenario_key: forecastScenarioKey, as_of_date: today })
      .then((res) => {
        if (cancelled) return
        setPacingCashWeeks(res?.weeks || null)
        setPacingAsOf(trimStr(res?.as_of_date) || today)
      })
      .catch((err: any) => {
        if (cancelled) return
        setPacingError(`Pacing could not load: ${trimStr(err?.message || err) || 'request failed.'}`)
        setPacingCashWeeks(null)
      })
      .finally(() => {
        if (!cancelled) setPacingLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [nativeReportingAnchor, grain, workbookId, forecastScenarioKey, appApi, proFeatures])

  const actualHistoryError = forecastStudioVisibleActualHistoryError({
    actualStatementError,
    actualBasisError,
    actualDetailCashError,
    statementKind,
    directCashForecast: isDirectCashForecast(forecast?.statement),
  })
  const asOfLabel = useMemo(
    () => formatAsOf(actualAnchorPeriod || null),
    [actualAnchorPeriod],
  )
  const forecastStatementForDisplay = forecast?.statement_type === statementKind && forecast?.statement?.statement_type === statementKind
    ? forecast.statement
    : null
  const mlDriverMetrics = useMemo(
    () => forecastDriverMetricsFromStatement(forecastStatementForDisplay),
    [forecastStatementForDisplay],
  )
  const mlDriverMetricSignature = `${mlDriverMetrics.grossMargin ?? ''}|${mlDriverMetrics.opexPct ?? ''}`
  const driverActualBasisStatement = statementKind === 'profit_and_loss' ? actualStatement : actualBasisStatement
  const displayAssumptions = useMemo(
    () => currentSource ? assumptions?.assumptions || forecast?.driver_assumptions || {} : observedDriverAssumptions(assumptions, comparisons, comparisonKind, driverActualBasisStatement, actualBalanceSheetStatement, mlDriverMetrics),
    [currentSource, forecast?.driver_assumptions, actualBalanceSheetStatement, assumptions, comparisonKind, comparisons, driverActualBasisStatement, mlDriverMetrics],
  )
  // The model baseline: the same generated-assumption merge with user
  // overrides stripped first, so override keys fall back to their
  // history-derived "auto" values.
  const baselineAssumptions = useMemo(
    () => currentSource ? stripDriverOverrides(forecast?.driver_assumptions || {}) : observedDriverAssumptions(
      assumptions ? { ...assumptions, assumptions: stripDriverOverrides(assumptions.assumptions) } : null,
      comparisons,
      comparisonKind,
      driverActualBasisStatement,
      actualBalanceSheetStatement,
      mlDriverMetrics,
    ),
    [currentSource, forecast?.driver_assumptions, actualBalanceSheetStatement, assumptions, comparisonKind, comparisons, driverActualBasisStatement, mlDriverMetrics],
  )
  const actualHistorySignature = useMemo(
    () => [
      actualBasisStatement?.periods?.[actualBasisStatement.periods.length - 1] || '',
      actualBasisStatement?.rows?.length || 0,
      actualBalanceSheetStatement?.periods?.[actualBalanceSheetStatement.periods.length - 1] || '',
      actualBalanceSheetStatement?.rows?.length || 0,
    ].join('|'),
    [actualBalanceSheetStatement, actualBasisStatement],
  )
  const packageHasStoredDriverAssumptions = hasStoredDriverAssumptions(assumptions)
  useEffect(() => {
    if (!baselineAssumptions || !assumptions) return
    const comparisonSignature = comparisons?.signature || comparisons?.as_of || 'no-comparisons'
    const key = `${assumptions.package?.id || 'none'}|${comparisonSignature}|${actualHistorySignature}|${mlDriverMetricSignature}|${packageHasStoredDriverAssumptions ? 'stored' : 'observed'}`
    if (baseAssumptionsKeyRef.current === key) return
    baseAssumptionsKeyRef.current = key
    setBaseAssumptions(baselineAssumptions)
  }, [actualHistorySignature, assumptions, baselineAssumptions, comparisons, mlDriverMetricSignature, packageHasStoredDriverAssumptions])
  const useDetailCashActuals = statementKind === 'cash_flow'
    && grain !== 'month'
    && isDirectCashForecast(forecastStatementForDisplay)
  const actualStatementForModel = useDetailCashActuals ? null : actualStatement
  const actualBasisStatementForModel = useDetailCashActuals
    ? actualDetailBasisStatement
    : actualBasisStatement
  const actualBalanceSheetSourceForModel = useDetailCashActuals
    ? actualDetailBalanceSheetStatement
    : actualBalanceSheetStatement
  const modelHistoryPeriodCount = currentSource ? forecast?.current_actual_statement?.periods.length || 0 : isDirectCashForecast(forecastStatementForDisplay)
    ? actualStatementForModel?.periods?.length || actualBasisStatementForModel?.periods?.length || 0
    : grain === 'month'
      ? actualStatement?.periods?.length || 0
      : 0
  const modelForecastPeriodCount = grain === 'month'
    ? futureMonths
    : forecastStatementForDisplay?.periods.length || 0
  const modelingStatement = useMemo(
    () => currentSource ? mergeCurrentForecast(forecast?.current_actual_statement || null, forecastStatementForDisplay, modelHistoryPeriodCount, modelForecastPeriodCount, forecast?.current_actual_source_identity, currentSource.source_identity) : mergeActualAndForecastStatements(actualStatementForModel, forecastStatementForDisplay, modelHistoryPeriodCount, modelForecastPeriodCount, actualBasisStatementForModel, actualBalanceSheetStatement, actualBalanceSheetSourceForModel),
    [currentSource, forecast?.current_actual_statement, forecast?.current_actual_source_identity, actualBalanceSheetSourceForModel, actualBalanceSheetStatement, actualBasisStatementForModel, actualStatementForModel, forecastStatementForDisplay, modelForecastPeriodCount, modelHistoryPeriodCount],
  )
  const anchorCashBalance = cashBalanceAtActualAnchor
  const pacingModel = useMemo<PacingModel>(() => {
    const today = new Date().toISOString().slice(0, 10)
    const empty: PacingModel = { available: false, grain: grain === 'month' ? 'month' : 'week', period: null, lines: [], asOfActualsLabel: '', warning: null }
    if (currentSource) return empty
    const asOfLabelForActuals = formatAsOf(pacingAsOf || today)

    if (grain === 'month') {
      const currentMonth = monthStartIso(today)
      if (actualAnchorPeriod && monthStartIso(actualAnchorPeriod) === currentMonth) return empty
      const period = buildPacingPeriod('month', currentMonth, today)
      if (!period) return empty
      const beginningCash = modeledCashQualified ? anchorCashBalance : null
      const actualNetCash = pacingActualCashBalance != null && beginningCash != null
        ? pacingActualCashBalance - beginningCash
        : null
      const lines: PacingLineInput[] = [
        {
          id: 'revenue', label: 'Revenue', kind: 'flow', favorable: 'higher',
          forecastFull: statementValueForMonth(pacingForecastPnl, [{ label: /^total income$/i }, { label: /^total revenue$/i }, { label: /^income$/i }], currentMonth),
          actualToDate: statementValueForMonth(pacingActualPnl, [{ label: /^total income$/i }, { label: /^total revenue$/i }, { label: /^income$/i }], currentMonth),
        },
        {
          id: 'expenses', label: 'Total expenses', kind: 'flow', favorable: 'lower',
          forecastFull: statementValueForMonth(pacingForecastPnl, [{ label: /^total expenses?$/i }], currentMonth),
          actualToDate: statementValueForMonth(pacingActualPnl, [{ label: /^total expenses?$/i }], currentMonth),
        },
        {
          id: 'net_income', label: 'Net income', kind: 'flow', favorable: 'higher',
          forecastFull: statementValueForMonth(pacingForecastPnl, [{ label: /^net income$/i }, { label: /^net operating income$/i }], currentMonth),
          actualToDate: statementValueForMonth(pacingActualPnl, [{ label: /^net income$/i }, { label: /^net operating income$/i }], currentMonth),
        },
        {
          id: 'net_cash_change', label: 'Net cash change', kind: 'flow', favorable: 'higher',
          forecastFull: statementValueForMonth(pacingForecastCash, [{ nodeId: 'fg-forecast-cash-net-change' }, { label: /^net cash change$/i }, { label: /^net change in cash$/i }], currentMonth),
          actualToDate: actualNetCash,
        },
        {
          id: 'ending_cash', label: 'Ending cash', kind: 'point_in_time', favorable: 'higher',
          forecastFull: statementValueForMonth(pacingForecastCash, [{ nodeId: 'fg-forecast-cash-ending-cash' }, { label: /^ending cash$/i }], currentMonth),
          actualToDate: pacingActualCashBalance,
        },
      ]
      return buildPacingModel({ grain: 'month', period, lines, asOfActualsLabel: asOfLabelForActuals })
    }

    if (grain === 'weekly' || grain === 'daily') {
      const weeks = pacingCashWeeks || []
      const current = weeks.find((week) => {
        const start = trimStr(week.week_start).slice(0, 10)
        return start && start <= today && today < addDaysIso(start, 7)
      }) || weeks.find((week) => weekStartIso(trimStr(week.week_start)) === weekStartIso(today)) || null
      if (!current) return empty
      const period = buildPacingPeriod('week', trimStr(current.week_start).slice(0, 10), today)
      if (!period) return empty
      const num = (value: string | null | undefined): number | null => {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
      }
      const openingCash = num(current.opening_cash)
      const actualNet = num(current.actual_net_cash_change)
      const lines: PacingLineInput[] = [
        { id: 'net_cash_change', label: 'Net cash change', kind: 'flow', favorable: 'higher', forecastFull: num(current.net_cash_change), actualToDate: actualNet },
        { id: 'receipts', label: 'Receipts', kind: 'flow', favorable: 'higher', forecastFull: num(current.receipts), actualToDate: null },
        { id: 'disbursements', label: 'Disbursements', kind: 'flow', favorable: 'lower', forecastFull: num(current.disbursements), actualToDate: null },
        { id: 'ending_cash', label: 'Ending cash', kind: 'point_in_time', favorable: 'higher', forecastFull: num(current.ending_cash), actualToDate: openingCash != null && actualNet != null ? openingCash + actualNet : null },
      ]
      return buildPacingModel({ grain: 'week', period, lines, asOfActualsLabel: asOfLabelForActuals })
    }

    return empty
  }, [currentSource, grain, actualAnchorPeriod, anchorCashBalance, pacingForecastPnl, pacingForecastCash, pacingActualPnl, pacingActualCashBalance, pacingCashWeeks, pacingAsOf])
  const adjustedModelResult = useMemo(() => {
    if (currentSource) return {
      statement: currentForecastPreview(modelingStatement, serverDriverPreview?.lines || null,
        forecastScenarioKey, String(valueCase), currentPreviewPending || Boolean(currentPreviewError)),
      refinedCellCount: serverDriverPreview?.changedLineCount || 0,
    }
    // Non-base scenarios render the package's own scenario lines untouched —
    // the driver-edit pipeline (and its server preview refinement) is a
    // base-scenario tool, and recalibrating an upside path to base drivers
    // would erase the scenario.
    if (forecastScenarioKey !== 'base') {
      return { statement: withDirectCashRollForward(modelingStatement, anchorCashBalance), refinedCellCount: 0 }
    }
    const driverAdjusted = applyForecastDriverDeltas(modelingStatement, displayAssumptions, baseAssumptions, {
      anchorReceiptsToActualGrowth: grain === 'month' && modelingStatement?.statement_type === 'cash_flow',
    })
    // Server preview lines replace the client estimate where they match; the
    // client recompute stays the first paint and the only fallback.
    const refined = serverDriverPreview
      ? applyServerDriverPreviewToStatement(driverAdjusted, serverDriverPreview.lines, 'base')
      : { statement: driverAdjusted, matchedCellCount: 0 }
    return { statement: withDirectCashRollForward(refined.statement, anchorCashBalance), refinedCellCount: refined.matchedCellCount }
  }, [currentSource, currentPreviewPending, currentPreviewError, valueCase, anchorCashBalance, baseAssumptions, displayAssumptions, forecastScenarioKey, grain, modelingStatement, serverDriverPreview])
  const adjustedModelStatement = adjustedModelResult.statement
  const serverPreviewRefinedCellCount = adjustedModelResult.refinedCellCount
  // One cash model: merge actuals + a per-case forecast, apply the live driver
  // deltas (base scenario only), and roll forward from the anchor. The displayed
  // statement, the trend-fan band, AND the Excel export all flow through this exact
  // path, so they can never disagree. (adjustedModelResult above is this same chain
  // for the selected case, plus the optional server-preview refinement.)
  const driverModelCashStatement = useCallback(
    (forecastStmt: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null) => {
      if (currentSource) return mergeCurrentForecast(forecast?.current_actual_statement || null, forecastStmt, modelHistoryPeriodCount, modelForecastPeriodCount, forecast?.current_actual_source_identity, currentSource.source_identity)
      const merged = mergeActualAndForecastStatements(actualStatementForModel, forecastStmt, modelHistoryPeriodCount, modelForecastPeriodCount, actualBasisStatementForModel, actualBalanceSheetStatement, actualBalanceSheetSourceForModel)
      if (forecastScenarioKey !== 'base') return withDirectCashRollForward(merged, anchorCashBalance)
      const adjusted = applyForecastDriverDeltas(merged, displayAssumptions, baseAssumptions, {
        anchorReceiptsToActualGrowth: grain === 'month' && merged?.statement_type === 'cash_flow',
      })
      return withDirectCashRollForward(adjusted, anchorCashBalance)
    },
    [currentSource, forecast?.current_actual_statement, forecast?.current_actual_source_identity, actualBalanceSheetSourceForModel, actualBalanceSheetStatement, actualBasisStatementForModel, actualStatementForModel, anchorCashBalance, baseAssumptions, displayAssumptions, forecastScenarioKey, grain, modelForecastPeriodCount, modelHistoryPeriodCount],
  )
  // The trend-fan band must reflect the SAME driver-modeled cash as the statement —
  // NOT the raw, unoriented per-case direct-cash rows (whose Low craters far out).
  const nativeComponentStatements = useMemo(() => currentSource ? currentComponentStatements(currentSource, forecast?.statement || null) : null, [currentSource, forecast?.statement])
  const nativeComponentCase = (valueCase: 'expected' | 'low' | 'high') => currentSource && nativeComponentStatements
    ? sliceStatementDisplayWindow(currentForecastPreview(driverModelCashStatement(nativeComponentStatements[valueCase]), serverDriverPreview?.lines || null, forecastScenarioKey, valueCase, currentPreviewPending || Boolean(currentPreviewError)), actualHistoryPeriods, futureMonths) : null
  const nativeExpectedCashStatement = nativeComponentCase('expected')
  const legacyBandStatements = useMemo(() => ({
      low: driverModelCashStatement(forecastBandStatements?.low || null),
      high: driverModelCashStatement(forecastBandStatements?.high || null),
    }), [driverModelCashStatement, forecastBandStatements])
  const driverModeledBandStatements = currentSource ? { low: nativeComponentCase('low'), high: nativeComponentCase('high') } : legacyBandStatements
  const adjustedStatementRaw = useMemo(
    () => grain === 'month'
      ? sliceStatementDisplayWindow(adjustedModelStatement, actualHistoryPeriods, futureMonths)
      : adjustedModelStatement?.statement_type === 'cash_flow'
        ? sliceStatementDisplayWindow(adjustedModelStatement, actualDetailBasisStatement?.periods.length || 0, adjustedModelStatement.periods.length)
      : adjustedModelStatement,
    [actualDetailBasisStatement, actualHistoryPeriods, adjustedModelStatement, futureMonths, grain],
  )
  const directCashActualBasisPending = Boolean(
    statementKind === 'cash_flow'
    && isDirectCashForecast(forecastStatementForDisplay)
    && !actualBasisError
    && (
      actualBasisLoading
      || !actualBasisStatement?.rows?.length
      || !actualBalanceSheetStatement?.rows?.length
    ),
  )
  const adjustedStatement = directCashActualBasisPending ? null : adjustedStatementRaw
  const profitAndLossFormulaIntegrity = useMemo(
    () => checkProfitAndLossRenderFormulaIntegrity(adjustedStatement),
    [adjustedStatement],
  )
  // Exact dollars, not compact: the check only fires above BOTH $1 and 0.5% drift,
  // so every violation is a real gap — but compact formatting rendered a genuine
  // ~$5K miss as "$999.9K vs $1.0M", which reads as a rounding glitch and teaches
  // the reader to distrust the warning instead of the statement.
  const profitAndLossFormulaIntegrityNotice = useMemo(() => {
    const violation = profitAndLossFormulaIntegrity.worstViolation
    if (!violation) return null
    const label = profitAndLossFormulaLabel(violation.check)
    const period = formatAsOf(violation.period)
    return {
      headline: `${label} doesn’t add up for ${period}, so we’re holding this statement back.`,
      detail: `${label} comes to ${formatCurrencyExact(violation.actual)}, but the lines above it total ${formatCurrencyExact(violation.expected)} — a gap of ${formatCurrencyExact(violation.absDiff)}. We’d rather show you nothing than a statement that doesn’t foot.`,
      nextStep: 'Rebuilding the forecast clears this most of the time. If it comes back after a rebuild, the gap is in how this workbook’s accounts are grouped — your accountant can repair that.',
    }
  }, [profitAndLossFormulaIntegrity])
  const statementWithheldForIntegrity = Boolean(profitAndLossFormulaIntegrityNotice)
  const anyDriverEdited = hasDriverOverrides(displayAssumptions)
  const baselineModelStatement = useMemo(
    () => !currentSource && forecastScenarioKey === 'base' && anyDriverEdited && baseAssumptions
      ? withDirectCashRollForward(applyForecastDriverDeltas(modelingStatement, baseAssumptions, baseAssumptions, {
        anchorReceiptsToActualGrowth: grain === 'month' && modelingStatement?.statement_type === 'cash_flow',
      }), anchorCashBalance)
      : null,
    [currentSource, anchorCashBalance, anyDriverEdited, baseAssumptions, forecastScenarioKey, grain, modelingStatement],
  )
  const baselineDisplayStatement = useMemo(
    () => {
      if (!baselineModelStatement) return null
      if (grain === 'month') return sliceStatementDisplayWindow(baselineModelStatement, actualHistoryPeriods, futureMonths)
      if (baselineModelStatement.statement_type === 'cash_flow') {
        return sliceStatementDisplayWindow(baselineModelStatement, actualDetailBasisStatement?.periods.length || 0, baselineModelStatement.periods.length)
      }
      return baselineModelStatement
    },
    [actualDetailBasisStatement, actualHistoryPeriods, baselineModelStatement, futureMonths, grain],
  )
  const statementDiff = useMemo(
    () => baselineDisplayStatement && adjustedStatement
      ? computeStatementDiff(baselineDisplayStatement, adjustedStatement)
      : null,
    [adjustedStatement, baselineDisplayStatement],
  )
  // Base-scenario comparison statement, run through the same merge/roll-forward/
  // slice pipeline as the active scenario (and, like it, without the driver
  // recalibration) so the diff isolates the scenario itself.
  const scenarioBaseStatementForDisplay = !currentSource && forecastScenarioKey !== 'base'
    && scenarioBaseForecast?.statement_type === statementKind
    && scenarioBaseForecast?.statement?.statement_type === statementKind
    ? scenarioBaseForecast.statement
    : null
  const scenarioBaseDisplayStatement = useMemo(() => {
    if (!scenarioBaseStatementForDisplay) return null
    const merged = mergeActualAndForecastStatements(
      actualStatementForModel,
      scenarioBaseStatementForDisplay,
      modelHistoryPeriodCount,
      modelForecastPeriodCount,
      actualBasisStatementForModel,
      actualBalanceSheetStatement,
      actualBalanceSheetSourceForModel,
    )
    const rolled = withDirectCashRollForward(merged, anchorCashBalance)
    if (grain === 'month') return sliceStatementDisplayWindow(rolled, actualHistoryPeriods, futureMonths)
    if (rolled?.statement_type === 'cash_flow') {
      return sliceStatementDisplayWindow(rolled, actualDetailBasisStatement?.periods.length || 0, rolled.periods.length)
    }
    return rolled
  }, [actualBalanceSheetSourceForModel, actualBalanceSheetStatement, actualBasisStatementForModel, actualDetailBasisStatement, actualHistoryPeriods, actualStatementForModel, anchorCashBalance, futureMonths, grain, modelForecastPeriodCount, modelHistoryPeriodCount, scenarioBaseStatementForDisplay])
  const scenarioCompareDiff = useMemo(
    () => forecastScenarioKey !== 'base' && scenarioBaseDisplayStatement && adjustedStatement
      ? computeStatementDiff(scenarioBaseDisplayStatement, adjustedStatement)
      : null,
    [adjustedStatement, forecastScenarioKey, scenarioBaseDisplayStatement],
  )
  const statementCellDecorator = useMemo(() => {
    const diff = statementDiff
    if (!diff?.hasChanges) return undefined
    return (row: QuickBooksFpaStatementRow, periodIndex: number) => {
      const delta = diff.cellDeltas[`${row.node_id}|${periodIndex}`]
      if (delta == null) return null
      return {
        tone: (delta >= 0 ? 'edited-up' : 'edited-down') as 'edited-up' | 'edited-down',
        title: `vs model baseline: ${formatSignedCurrency(delta)}`,
      }
    }
  }, [statementDiff])
  const periodHeaderFormatter = useMemo(() => {
    const baseFormatter = forecastPeriodHeader(grain)
    const kinds = (adjustedStatement as any)?.period_kinds as string[] | undefined
    return (period: string | null | undefined, index: number) => {
      const label = grain === 'month' ? forecastMonthYearHeader(period) : baseFormatter(period)
      const kind = kinds?.[index]
      const kindLabel = kind === 'actual' ? 'Actual' : kind === 'forecast' ? 'Forecast' : ''
      if (kindLabel) {
        return (
          <span className="cfo-statement-period-header" aria-label={`${kindLabel} ${label}`}>
            <span className="cfo-statement-period-header-kind">{kindLabel}</span>
            <span className="cfo-statement-period-header-date">{label}</span>
          </span>
        )
      }
      return label
    }
  }, [adjustedStatement, grain])
  const openExplainForCell = (cell: CfoStatementGlDetailCell) => {
    const period = trimStr(cell.period).slice(0, 10)
    if (!period || !cell.nodeId) return
    const kinds = ((adjustedStatement as any)?.period_kinds || []) as string[]
    const periodIndex = (adjustedStatement?.periods || []).findIndex((candidate) => String(candidate || '').slice(0, 10) === period)
    if (kinds.length && periodIndex >= 0 && kinds[periodIndex] !== 'forecast') return
    setExplainTarget({
      nodeId: cell.nodeId,
      nodeLabel: cell.rowLabel || cell.accountName || cell.nodeId,
      period,
      statementType: trimStr(cell.statementType) || statementKind,
    })
  }
  const explainHistory = useMemo<CfoForecastExplainHistoryPoint[]>(() => {
    if (!explainTarget || !modelingStatement?.periods?.length) return []
    const kinds = ((modelingStatement as any)?.period_kinds || []) as string[]
    if (!kinds.length) return []
    const row = modelingStatement.rows?.find((candidate) =>
      candidate.node_id === explainTarget.nodeId
      || /:source:(.+)$/.exec(candidate.node_id || '')?.[1] === explainTarget.nodeId)
    if (!row) return []
    const points: CfoForecastExplainHistoryPoint[] = []
    kinds.forEach((kind, index) => {
      if (kind !== 'actual') return
      const value = Number(row.values?.[index])
      points.push({
        period: String(modelingStatement.periods?.[index] || '').slice(0, 10),
        value: Number.isFinite(value) ? value : null,
      })
    })
    return points.slice(-12)
  }, [explainTarget, modelingStatement])
  const nodeReviewStates = useMemo(
    () => forecastNodeReviewStates(forecastDeltas, forecastScenarioKey),
    [forecastDeltas, forecastScenarioKey],
  )
  const flaggedNodeCount = useMemo(
    () => Object.values(nodeReviewStates).filter((state) => state === 'flagged').length,
    [nodeReviewStates],
  )
  const statementRowBadge = useMemo(() => {
    if (!Object.keys(nodeReviewStates).length) return undefined
    return (row: QuickBooksFpaStatementRow) => {
      const state = nodeReviewStates[/:source:(.+)$/.exec(row.node_id || '')?.[1] || row.node_id]
      if (!state) return null
      const glyph = state === 'approved' ? '✓' : state === 'flagged' ? '⚑' : '…'
      const label = state === 'approved' ? 'Approved' : state === 'flagged' ? 'Flagged' : 'Needs discussion'
      return (
        <span className={`cfo-forecast-review-dot ${state}`} role="img" title={`${label} in review`} aria-label={`${label} in review`}>
          {glyph}
        </span>
      )
    }
  }, [nodeReviewStates])
  const statementForTable = useMemo(() => {
    if (statementWithheldForIntegrity) return null
    if (!flaggedOnly || !adjustedStatement) return adjustedStatement
    return {
      ...adjustedStatement,
      rows: (adjustedStatement.rows || []).filter((row) =>
        nodeReviewStates[/:source:(.+)$/.exec(row.node_id || '')?.[1] || row.node_id] === 'flagged'),
    }
  }, [adjustedStatement, flaggedOnly, nodeReviewStates, statementWithheldForIntegrity])
  const staleDeltas = useMemo(() => {
    if (!staleDeltaKeys.length) return []
    const keys = new Set(staleDeltaKeys)
    return forecastDeltas.filter((delta) => keys.has(delta.business_key))
  }, [forecastDeltas, staleDeltaKeys])
  const staleDeltaLabel = (delta: QuickBooksFpaMlUserDelta): string => {
    if (delta.target_kind === 'line') {
      return `${(trimStr(delta.statement_type) || 'line').replace(/_/g, ' ')} · ${trimStr(delta.node_id)} · ${formatAsOf(delta.period_start)}`
    }
    if (delta.target_kind === 'cash_week') {
      return `${trimStr(delta.cash_event_type) || 'cash'} · week of ${formatAsOf(delta.week_start)}`
    }
    if (delta.target_kind === 'driver') {
      return `driver ${trimStr(delta.driver_key)} · ${formatAsOf(delta.period_start)}`
    }
    return delta.business_key
  }
  const deleteStaleDelta = async (delta: QuickBooksFpaMlUserDelta) => {
    const wb = trimStr(workbookId)
    if (!wb || !delta?.id || staleDeltaBusyId) return
    setStaleDeltaBusyId(delta.id)
    try {
      await appApi.deleteQuickBooksFpaMlDelta(wb, delta.id)
      setForecastDeltas((current) => current.filter((existing) => existing.id !== delta.id))
      setStaleDeltaKeys((current) => current.filter((key) => key !== delta.business_key))
    } catch (err: any) {
      setSaving(trimStr(err?.message || err) || 'Could not remove the stale edit.')
    } finally {
      setStaleDeltaBusyId('')
    }
  }
  const setExplainReviewState = async (state: ForecastReviewState) => {
    const wb = trimStr(workbookId)
    const target = explainTarget
    if (!wb || !target) return
    setExplainReviewBusy(true)
    setExplainReviewStatus('')
    try {
      const delta = await appApi.patchQuickBooksFpaMlLineDeltaReview(wb, {
        scenario_key: forecastScenarioKey,
        period_grain: 'month',
        statement_type: target.statementType,
        node_id: target.nodeId,
        period_start: target.period,
        status: state,
        package_id: forecastPackageId || null,
      })
      const nextDeltas = [
        ...forecastDeltas.filter((existing) => existing.business_key !== delta.business_key),
        delta,
      ]
      setForecastDeltas(nextDeltas)
      setExplainLine((current) => (current
        ? { ...current, feedback_state: delta.feedback_state, review_note: delta.review_note ?? current.review_note }
        : current))
      // Keep the DuckDB overlay in sync so the next `_effective` query reads
      // the same review state the server just acknowledged.
      await warmUserDeltasInDuckDb(workerClient, nextDeltas, duckDbOpts).catch(() => undefined)
      setExplainReviewStatus(`Marked ${state.replace(/_/g, ' ')}.`)
    } catch (err: any) {
      setExplainReviewStatus(trimStr(err?.message || err) || 'Review update failed.')
    } finally {
      setExplainReviewBusy(false)
    }
  }
  const packageBuildNotice = forecastStudioPackageBuildNotice(packageBuild)
  const packageBuildBusy = packageBuild?.status === 'queued' || packageBuild?.status === 'running'
  const forecastDisplayLoading = loading || bootstrapLoading || directCashActualBasisPending
  const priorForecastLabel = null
  const selectedBeyondBuiltCoverage = Boolean(
    trimStr(selectedEndPeriod)
    && forecastPackageSourceWindowEnd
    && trimStr(selectedEndPeriod).slice(0, 7) > forecastPackageSourceWindowEnd.slice(0, 7),
  )
  const handleBuildAction = (kind: ForecastBuildFailureActionKind) => {
    if (kind === 'pick_prior_month') {
      const fallback = defaultForecastAnchor(periodOptions, anchorSafeSourceWindowEnd || null, new Date().toISOString().slice(0, 10))
        || monthOffsetIso(trimStr(selectedEndPeriod), -1)
      if (fallback) onSelectedEndPeriodChange?.(fallback)
      return
    }
    // 'sync' and 'retry' both re-run the ensure pipeline, which kicks the
    // backend sync + package build for the selected month.
    setEnsureRetryNonce((nonce) => nonce + 1)
  }
  const packageLabel = packageBuildNotice
    ? packageBuildNotice.tone === 'building' ? 'Preparing forecast package…' : 'Forecast package unavailable'
    : forecastStudioRenderedPackageLabel(forecast)
  const rowCount = adjustedStatement?.rows?.length || forecast?.statement?.rows?.length || 0
  const periodCount = adjustedStatement?.periods?.length || forecast?.statement?.periods?.length || 0
  const forecastSubtitleAnchor = actualAnchorPeriod
    ? nativeReportingAnchor
      ? ` Anchored to ${formatAsOf(actualAnchorPeriod)}, the last complete reporting month for this forecast.`
      : ` Anchored to ${formatAsOf(actualAnchorPeriod)}, your most recent closed month.`
    : ''
  const validationStatus = packageBuild
    ? packageBuild.status
    : trimStr(forecastCacheStatus || forecast?.validation?.status || '') || (rowCount ? 'ok' : 'empty')
  const forecastUsesMaterializedArtifacts = forecast?.validation?.rollup_basis === 'materialized_parquet_artifacts'
  const forecastPageCacheState = useMemo(
    () => forecastCacheStateForPage(cacheState, forecastUsesMaterializedArtifacts, forecastPackageId),
    [cacheState, forecastPackageId, forecastUsesMaterializedArtifacts],
  )
  const forecastUsesLegacyMonthlyRows = forecast?.validation?.monthly_line_backed === true
    || forecast?.validation?.rollup_basis === 'legacy_monthly_line_metadata'
  // The server emits six `rollup_basis` values; this page only ever recognized two,
  // so a perfectly good `monthly_control` package fell through to "not available
  // yet" while the same card said "Up to date with your books" and showed hundreds
  // of rendered rows. Absence of rows — not an unrecognized provenance stamp — is
  // what actually means there is nothing to look at.
  const forecastPackageEmpty = !packageBuildBusy && !rowCount
  const cashDegradedNotice = degradedCashNotice(forecast?.validation, grain, forecastUsesLegacyMonthlyRows)
  const receiptsRow = adjustedStatement?.rows?.find((row) => row.node_id === 'fg-forecast-cash-receipts')
    || adjustedStatement?.rows?.find((row) => /^receipts$/i.test(row.label || ''))
  const netCashRow = currentSource ? adjustedStatement?.rows?.find(row => row.node_id === 'cash_flow:net_change_cash') : adjustedStatement?.rows?.find((row) => row.node_id === 'fg-forecast-cash-net-change')
    || adjustedStatement?.rows?.find((row) => /^net cash change$/i.test(row.label || ''))
    || adjustedStatement?.rows?.find((row) => /^net change in cash$/i.test(row.label || ''))
  const endingCashRow = currentSource ? adjustedStatement?.rows?.find(row => row.node_id === 'cash_flow:ending_cash') : adjustedStatement?.rows?.find((row) => row.node_id === 'fg-forecast-cash-ending-cash')
    || adjustedStatement?.rows?.find((row) => /^ending cash$/i.test(row.label || ''))
  const periodKinds = ((adjustedStatement as any)?.period_kinds || []) as string[]
  const actualValueIndexes = periodKinds.length
    ? periodKinds.map((kind, index) => kind === 'actual' ? index : -1).filter((index) => index >= 0)
    : []
  const forecastValueIndexes = periodKinds.length ? periodKinds.map((kind, index) => kind === 'forecast' ? index : -1).filter((index) => index >= 0) : (adjustedStatement?.periods || []).map((_period, index) => index)
  const nativeModeledCash = currentSource ? currentModeledCash(currentSource, nativeExpectedCashStatement, driverModeledBandStatements.low || null, driverModeledBandStatements.high || null) : null
  const nearTermCash = currentSource ? nativeModeledCash?.nearTermCash ?? null : modeledCashQualified && netCashRow
    ? forecastValueIndexes.slice(0, grain === 'month' ? futureMonths : forecastValueIndexes.length).reduce<number>((sum, index) => sum + (Number(netCashRow.values?.[index]) || 0), 0)
    : null
  const beginningCash = modeledCashQualified ? anchorCashBalance : null
  const endingCash = currentSource ? nativeModeledCash?.endingCash ?? null : beginningCash != null && nearTermCash != null ? beginningCash + nearTermCash : null
  const detailActualPeriodCount = grain === 'month' ? actualHistoryPeriods : actualDetailBasisStatement?.periods.length || 0
  const visibleActualIndexes = detailActualPeriodCount > 0 ? actualValueIndexes.slice(-detailActualPeriodCount) : []
  const visibleForecastIndexes = forecastValueIndexes.slice(0, grain === 'month' ? futureMonths : forecastValueIndexes.length)
  const trendIndexes = [...visibleActualIndexes, ...visibleForecastIndexes]
  const trendPeriods = trendIndexes.map((index) => adjustedStatement?.periods?.[index] || null)
  const trendKinds = trendIndexes.map((index): ForecastTrendPointKind => periodKinds[index] === 'actual' ? 'actual' : 'forecast')
  const forecastTrendLabels = trendPeriods.map((period) => compactTrendPeriodLabel(period, grain))
  const receiptsTrendValues = rowTrendValues(receiptsRow, trendIndexes)
  const netCashTrendValues = rowTrendValues(netCashRow, trendIndexes)
  const directEndingCashTrendValues = rowTrendValues(endingCashRow, trendIndexes)
  const endingCashTrendValues = currentSource ? trendIndexes.map(index => nativeModeledCash?.values[index] ?? null) : finiteForecastValues(directEndingCashTrendValues).length >= 2
    ? directEndingCashTrendValues
    : (() => {
      if (beginningCash == null || !Number.isFinite(beginningCash) || !netCashRow) return directEndingCashTrendValues
      let rollingCash = beginningCash
      return trendIndexes.map((index) => {
        const value = Number(netCashRow.values?.[index])
        if (!Number.isFinite(value)) return null
        rollingCash += value
        return rollingCash
      })
    })()
  // Non-base scenarios overlay the base scenario as the dotted baseline; on
  // base, the overlay is the pre-driver-edit model when edits exist.
  const baselineStatementForTrend = currentSource ? null : forecastScenarioKey !== 'base'
    ? scenarioBaseDisplayStatement
    : statementDiff?.hasChanges ? baselineDisplayStatement : null
  const baselineOverlayValues = (values: Array<number | null>): Array<number | null> =>
    values.map((value, offset) => (trendKinds[offset] === 'forecast' ? value : null))
  const baselineReceiptsTrendValues = rowTrendValues(trendRow(baselineStatementForTrend, 'fg-forecast-cash-receipts', /^receipts$/i), trendIndexes)
  const baselineNetCashTrendValues = rowTrendValues(trendRow(baselineStatementForTrend, 'fg-forecast-cash-net-change', /^net (cash )?change/i), trendIndexes)
  const directBaselineEndingCashTrendValues = rowTrendValues(trendRow(baselineStatementForTrend, 'fg-forecast-cash-ending-cash', /^ending cash$/i), trendIndexes)
  const baselineEndingCashTrendValues = finiteForecastValues(directBaselineEndingCashTrendValues).length >= 2
    ? directBaselineEndingCashTrendValues
    : rollingEndingCashTrendValues(beginningCash, baselineNetCashTrendValues)
  const baselineTrendValuesByMetric: Record<string, Array<number | null>> = baselineStatementForTrend
    ? {
      receipts: baselineOverlayValues(baselineReceiptsTrendValues),
      'net-cash-change': baselineOverlayValues(baselineNetCashTrendValues),
      'ending-cash': baselineOverlayValues(baselineEndingCashTrendValues),
    }
    : {}
  const lowStatement = driverModeledBandStatements.low || null
  const highStatement = driverModeledBandStatements.high || null
  const lowReceiptsRow = trendRow(lowStatement, 'fg-forecast-cash-receipts', /^receipts$/i)
  const highReceiptsRow = trendRow(highStatement, 'fg-forecast-cash-receipts', /^receipts$/i)
  const lowNetCashRow = trendRow(lowStatement, 'fg-forecast-cash-net-change', /^net (cash )?change/i)
  const highNetCashRow = trendRow(highStatement, 'fg-forecast-cash-net-change', /^net (cash )?change/i)
  const lowEndingCashRow = trendRow(lowStatement, 'fg-forecast-cash-ending-cash', /^ending cash$/i)
  const highEndingCashRow = trendRow(highStatement, 'fg-forecast-cash-ending-cash', /^ending cash$/i)
  const lowReceiptsTrendValues = rowTrendValuesForPeriods(lowReceiptsRow, lowStatement?.periods || [], trendPeriods)
  const highReceiptsTrendValues = rowTrendValuesForPeriods(highReceiptsRow, highStatement?.periods || [], trendPeriods)
  const lowNetCashTrendValues = rowTrendValuesForPeriods(lowNetCashRow, lowStatement?.periods || [], trendPeriods)
  const highNetCashTrendValues = rowTrendValuesForPeriods(highNetCashRow, highStatement?.periods || [], trendPeriods)
  const directLowEndingCashTrendValues = rowTrendValuesForPeriods(lowEndingCashRow, lowStatement?.periods || [], trendPeriods)
  const directHighEndingCashTrendValues = rowTrendValuesForPeriods(highEndingCashRow, highStatement?.periods || [], trendPeriods)
  const rawLowEndingCashTrendValues = finiteForecastValues(directLowEndingCashTrendValues).length >= 2
    ? directLowEndingCashTrendValues
    : rollingEndingCashTrendValues(beginningCash, lowNetCashTrendValues)
  const rawHighEndingCashTrendValues = finiteForecastValues(directHighEndingCashTrendValues).length >= 2
    ? directHighEndingCashTrendValues
    : rollingEndingCashTrendValues(beginningCash, highNetCashTrendValues)
  const endingCashBandTrendValues = currentSource ? {
    lowValues: trendIndexes.map(index => nativeModeledCash?.lowValues[index] ?? null),
    highValues: trendIndexes.map(index => nativeModeledCash?.highValues[index] ?? null),
  } : modeledCashQualified ? cumulativeEndingCashBandValues({
    endingCashValues: endingCashTrendValues,
    lowEndingCashValues: rawLowEndingCashTrendValues,
    highEndingCashValues: rawHighEndingCashTrendValues,
    netCashValues: netCashTrendValues,
    lowNetCashValues: lowNetCashTrendValues,
    highNetCashValues: highNetCashTrendValues,
    kinds: trendKinds,
  }) : { lowValues: trendKinds.map(() => null), highValues: trendKinds.map(() => null) }
  const forecastTrendMetricCandidates: ForecastTrendMetric[] = [
    { id: 'receipts', label: 'Receipts', values: receiptsTrendValues, lowValues: lowReceiptsTrendValues, highValues: highReceiptsTrendValues, labels: forecastTrendLabels, kinds: trendKinds, tone: 'receipts' },
    { id: 'net-cash-change', label: 'Net cash change', values: netCashTrendValues, lowValues: lowNetCashTrendValues, highValues: highNetCashTrendValues, labels: forecastTrendLabels, kinds: trendKinds, tone: 'net' },
    { id: 'ending-cash', label: 'Ending cash', values: endingCashTrendValues, lowValues: endingCashBandTrendValues.lowValues, highValues: endingCashBandTrendValues.highValues, labels: forecastTrendLabels, kinds: trendKinds, tone: 'ending' },
  ]
  const forecastTrendMetrics = modeledCashQualified ? forecastTrendMetricCandidates.filter((metric) => finiteForecastValues(metric.values).length >= 2) : []
  const forecastInstantCards = useMemo(
    () => buildForecastInstantCards({
      packageId: forecastPackageId,
      validationStatus,
      actualAnchorPeriod,
      asOfLabel,
      comparisonKind,
      statementKind,
      grain,
      periodCount,
      endingCash,
      nearTermCash,
      labels: forecastTrendLabels,
      periods: trendPeriods,
      trendKinds,
      endingCashValues: endingCashTrendValues,
      comparisons,
    }),
    [actualAnchorPeriod, asOfLabel, comparisonKind, comparisons, endingCash, endingCashTrendValues, forecastPackageId, forecastTrendLabels, grain, nearTermCash, periodCount, statementKind, trendKinds, trendPeriods, validationStatus],
  )
  const forecastPageFactsFingerprint = useMemo(
    () => forecastSourceFingerprint({
      packageId: forecastPackageId,
      validationStatus,
      actualAnchorPeriod,
      statementKind,
      grain,
      endingCash,
      values: endingCashTrendValues,
    }),
    [actualAnchorPeriod, endingCash, endingCashTrendValues, forecastPackageId, grain, statementKind, validationStatus],
  )
  const accuracyPackage = forecastVsActual?.package || forecastSnapshot?.package || forecast?.package || null
  const accuracyReadiness = useMemo(
    () => readinessFromValidation(
      accuracyPackage?.validation_json || {},
      accuracyPackage?.data_coverage_json || {},
      accuracyPackage?.status ?? null,
    ),
    [accuracyPackage],
  )
  const accuracySummary = useMemo(
    () => (forecastVsActual
      ? forecastAccuracySummary(forecastVsActual.rows || [], {
        readinessStatus: accuracyReadiness.status,
        basisLabels: accuracyReadiness.basisLabels,
      })
      : null),
    [forecastVsActual, accuracyReadiness],
  )
  const trendBandSource = useMemo(
    () => currentSource?.range_semantics === 'dependency_preserving_component_range_envelope'
      ? 'component-envelope' as const : forecastBandSource(forecast?.package?.validation_json ?? forecastSnapshot?.package?.validation_json),
    [currentSource?.range_semantics, forecast?.package, forecastSnapshot?.package],
  )
  const driverInputIntentRef = useRef('')
  const driverInputIntent = JSON.stringify({ workbookId, sessionIdentity, packageId: forecastPackageId, scenario: forecastScenarioKey, anchor: actualAnchorPeriod })
  const liveDriverInputIntentRef = useRef(driverInputIntent)
  liveDriverInputIntentRef.current = driverInputIntent
  const driverSaveNonceRef = useRef(createRequestNonce())
  const persistDriverAssumptionsRef = useRef<(next: any) => void>(() => {})
  persistDriverAssumptionsRef.current = (next: any) => {
    if (driverInputIntentRef.current !== driverInputIntent || accessSessionIdentity(appApi.getToken?.()) !== sessionIdentity) return
    const wb = trimStr(workbookId)
    if (!wb) return
    if (currentSource && !forecastPackageId) {
      setSaving('No saved assumption record is selected. This what-if is unsaved; choose a saved scenario before saving.')
      return
    }
    const nonce = driverSaveNonceRef.current.next()
    const saveIntent = driverInputIntent
    const saveSession = sessionIdentity
    const responseIsCurrent = () => driverSaveNonceRef.current.isCurrent(nonce)
      && liveDriverInputIntentRef.current === saveIntent
      && accessSessionIdentity(appApi.getToken?.()) === saveSession
      && !driverSaveDebounceRef.current.pending()
    void appApi.updateQuickBooksFpaMlDriverAssumptions(wb, next, { package_id: forecastPackageId || undefined, scenario_key: forecastScenarioKey })
      .then((out) => {
        if (!responseIsCurrent()) return
        setAssumptions(out)
        setSaving('Assumptions saved.')
      })
      .catch((err: any) => {
        if (!responseIsCurrent()) return
        setSaving(trimStr(err?.message || err) || 'Assumption save failed.')
      })
  }
  const driverSaveDebounceRef = useRef(
    createTrailingDebounce((next: any) => persistDriverAssumptionsRef.current(next), DRIVER_ASSUMPTION_SAVE_DEBOUNCE_MS),
  )
  useEffect(() => () => {
    driverSaveDebounceRef.current.flush()
    driverSaveNonceRef.current.next()
    liveDriverInputIntentRef.current = ''
  }, [])

  // Server-refined driver preview: after the edit burst settles, replay the
  // exact engine generation with the current user overrides. The response is
  // nonce-guarded (a stale response for older assumptions is discarded) and
  // every failure — including the 409 for packages that predate the feature
  // snapshot — degrades silently to the client estimate.
  const driverPreviewNonceRef = useRef(createRequestNonce())
  const requestDriverPreviewRef = useRef<(next: any) => void>(() => {})
  requestDriverPreviewRef.current = (next: any) => {
    if (driverInputIntentRef.current !== driverInputIntent || accessSessionIdentity(appApi.getToken?.()) !== sessionIdentity) return
    const wb = trimStr(workbookId)
    const overrides: Record<string, any> = {}
    Object.entries(next || {}).forEach(([key, entry]) => {
      if ((entry as any)?.source === 'user_override') overrides[key] = entry
    })
    if (!wb || !Object.keys(overrides).length) {
      driverPreviewNonceRef.current.next()
      setServerDriverPreview(null)
      if (currentSource) { setCurrentPreviewPending(false); setCurrentPreviewError('') }
      return
    }
    const nonce = driverPreviewNonceRef.current.next()
    if (currentSource) { setCurrentPreviewPending(true); setCurrentPreviewError('') }
    const previewSource = currentSource
    const previewSession = sessionIdentity
    const requestStartedAt = Date.now()
    appApi.previewQuickBooksFpaMlForecastDrivers(wb, { packageId: forecastPackageId || null, assumptions: overrides, scenarioKey: forecastScenarioKey, asOfDate: actualAnchorPeriod || null, futureMonths })
      .then((out) => {
        if (accessSessionIdentity(appApi.getToken?.()) !== previewSession) return
        if (driverPreviewNonceRef.current.isCurrent(nonce)) {
          if (previewSource && !currentPreviewMatches(previewSource, out.current_source)) {
            setServerDriverPreview(null); setCurrentPreviewPending(false)
            setCurrentPreviewError('The source or approved model changed during the what-if. Reload before applying it.')
            return
          }
          setCurrentPreviewPending(false); setCurrentPreviewError('')
          setServerDriverPreview({
            lines: out.lines || [],
            computedInMs: Number(out.computed_in_ms) || 0,
            changedLineCount: Number(out.changed_line_count) || 0,
          })
        }
        forecastStudioPerf('driver_preview_done', {
          package_id: out.package_id || null,
          stale: !driverPreviewNonceRef.current.isCurrent(nonce),
          changed_lines: out.changed_line_count,
          computed_in_ms: out.computed_in_ms,
          elapsed_ms: Date.now() - requestStartedAt,
        })
      })
      .catch((err: any) => {
        forecastStudioPerf('driver_preview_failed', {
          package_id: forecastPackageId || null,
          message: trimStr(err?.message || err),
          elapsed_ms: Date.now() - requestStartedAt,
        })
        if (accessSessionIdentity(appApi.getToken?.()) !== previewSession) return
        if (driverPreviewNonceRef.current.isCurrent(nonce)) {
          setServerDriverPreview(null)
          if (previewSource) { setCurrentPreviewPending(false); setCurrentPreviewError(trimStr(err?.message || err) || 'Current what-if is unavailable.') }
        }
      })
  }
  const driverPreviewDebounceRef = useRef(
    createTrailingDebounce((next: any) => requestDriverPreviewRef.current(next), DRIVER_PREVIEW_DEBOUNCE_MS),
  )
  useEffect(() => () => driverPreviewDebounceRef.current.cancel(), [])
  // A different package, scenario, or anchor invalidates any in-flight or
  // applied preview outright.
  useEffect(() => {
    driverPreviewNonceRef.current.next()
    setServerDriverPreview(null)
  }, [forecastPackageId, scenarioKey, selectedEndPeriod, sessionIdentity])

  const applyDriverAssumptions = (next: any, { flush = false }: { flush?: boolean } = {}) => {
    if (!trimStr(workbookId)) return
    driverInputIntentRef.current = driverInputIntent
    driverSaveNonceRef.current.next()
    if (currentSource) { setCurrentPreviewPending(true); setCurrentPreviewError('') }
    setAssumptions((current) => ({ package: current?.package || forecast?.package || null, assumptions: next }))
    setSaving('Autosaving assumptions...')
    // Each new edit drops the previous server refinement so the instant
    // client recompute is what renders until a fresh preview lands.
    driverPreviewNonceRef.current.next()
    setServerDriverPreview(null)
    driverSaveDebounceRef.current.schedule(next)
    driverPreviewDebounceRef.current.schedule(next)
    if (flush) driverSaveDebounceRef.current.flush()
  }

  const saveDriver = (key: string, value: number) => {
    applyDriverAssumptions(patchDriver(displayAssumptions, key, value))
  }

  const revertDriver = (key: string) => {
    if (!baselineAssumptions?.[key]) return
    applyDriverAssumptions(revertDriverAssumption(displayAssumptions, baselineAssumptions, key), { flush: true })
  }

  const resetAllDrivers = () => {
    if (!baselineAssumptions) return
    setWhatIfLabel('')
    applyDriverAssumptions(revertAllDriverAssumptions(displayAssumptions, baselineAssumptions), { flush: true })
  }

  // '+ What if…': parse simple driver phrases client-side and push them
  // through the existing driver-edit pipeline (instant recompute, debounced
  // persistence, server refine). No AI calls.
  const handleWhatIfSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault()
    const parsed = parseWhatIfPrompt(whatIfText)
    if (!parsed) {
      setWhatIfHint("Couldn't read that — try e.g. 'growth at 12%'")
      return
    }
    // Native scoring keeps the explicitly selected authored scenario.
    if (!currentSource && scenarioKey !== 'base') setScenarioKey('base')
    let next = displayAssumptions
    Object.entries(parsed.assumptions).forEach(([key, value]) => {
      next = patchDriver(next, key, value)
    })
    applyDriverAssumptions(next)
    setWhatIfLabel(describeWhatIfAssumptions(parsed.assumptions))
    setWhatIfHint('')
    setWhatIfText('')
  }

  const scenarioBandMeta = useMemo(
    () => parseScenarioBandMeta(forecastSnapshot?.package?.validation_json ?? forecast?.package?.validation_json),
    [forecast?.package, forecastSnapshot?.package],
  )
  const availableScenarioKeys = useMemo(
    () => currentSource ? ['base', 'upside', 'downside'] : packageScenarioKeys(forecastSnapshot?.package || forecast?.package || null),
    [currentSource, forecast?.package, forecastSnapshot?.package],
  )
  const handleScenarioSelect = (key: string) => {
    if (key === scenarioKey) return
    setScenarioBaseForecast(null)
    setScenarioKey(key)
  }
  const actualHistoryUnitLabel = actualHistoryGrain === 'weekly' ? 'wks' : actualHistoryGrain === 'daily' ? 'days' : 'mo'
  const actualHistoryAriaLabel = actualHistoryGrain === 'weekly'
    ? 'Actual history weeks'
    : actualHistoryGrain === 'daily'
      ? 'Actual history days'
      : 'Actual history months'

  const runAutopilot = async () => {
    const wb = trimStr(workbookId)
    if (!wb) return
    setSaving('Generating scenarios...')
    try {
      const out = await appApi.generateQuickBooksFpaMlScenarioAutopilot(wb, {
        package_id: forecast?.package?.id,
        prompt: 'Create practical short-term cash scenarios using DSO, DPO, sales, and controllable expense drivers.',
        scenario_count: 3,
      })
      setAutopilot(out)
      setSaving('Autopilot scenarios refreshed.')
    } catch (err: any) {
      setSaving(trimStr(err?.message || err) || 'Scenario generation failed.')
    }
  }

  // Harvest the ONLINE model's displayed cash forecast for all three value cases by
  // running the page's REAL pipeline — no replica, no parallel model. This is the
  // exact adjustedModelStatement chain the screen renders: per-case monthly
  // direct-cash load → merge with actuals → applyForecastDriverDeltas (receipts
  // re-anchored to the actual run-rate) → withDirectCashRollForward from the
  // reconciled anchor cash. The export seeds these outputs as the workbook baseline;
  // the Drivers sheet flexes around them. Any miss returns null and the export falls
  // back to the Balance-Sheet-derived cash.
  const harvestDisplayedCashByCase = async (
    wb: string,
    harvestGrain: 'month' | 'weekly' | 'daily' = 'month',
  ): Promise<Partial<Record<'high' | 'expected' | 'low', QuickBooksFpaStatementRender | null>> | null> => {
    if (!workerClient) return null
    const months = Math.max(3, futureMonths)
    let snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse | null =
      forecastSnapshot && snapshotHasParquetArtifacts(forecastSnapshot) ? forecastSnapshot : null
    if (!snapshot) {
      snapshot = await appApi.getQuickBooksFpaMlForecastPackageSnapshot(wb, {
        package_id: selectedForecastPackageId ? forecastPackageId : undefined,
        scenario_key: scenarioKey === 'base' ? undefined : scenarioKey,
        as_of_date: selectedEndPeriod,
        grain: 'month',
        future_months: months,
        daily_limit: 50000,
      }, { timeoutMs: FORECAST_STUDIO_SNAPSHOT_TIMEOUT_MS }).catch(() => null)
    }
    if (!snapshot?.package || !snapshotHasParquetArtifacts(snapshot)) return null
    const warmCommon = {
      timeoutMs: FORECAST_STUDIO_CASH_ARTIFACT_TIMEOUT_MS,
      phase: 'export_cash_bands',
      inFlight: forecastArtifactWarmInFlightRef.current,
      completed: warmedForecastArtifactKeysRef.current,
    }
    if (snapshotSupportsMonthlyLineMetadata(snapshot)) {
      await warmForecastPackageForQueries(appApi, workerClient, wb, snapshot, duckDbOpts, ['line_metadata', 'layout_node'], {
        ...warmCommon,
        scenarioKey: snapshot.scenario_key || snapshot.package?.active_scenario_key || null,
      })
    } else {
      await warmForecastSnapshotInDuckDb(appApi, workerClient, wb, snapshot, duckDbOpts, { ...warmCommon, artifactKeys: ['cash_daily'] })
    }
    const build = async (vc: 'high' | 'expected' | 'low'): Promise<QuickBooksFpaStatementRender | null> => {
      const direct = await loadDirectCashForecastFromDuckDb(workerClient!, snapshot!, harvestGrain, vc, selectedEndPeriod, months, duckDbOpts).catch(() => null)
      if (!direct?.statement) return null
      // Monthly flows through the one-cash-model driver pipeline (matches the on-screen
      // statement). Weekly/daily are already timing-aware, bucketed, roll-forward-complete
      // from the dated cash_daily artifact — return them raw (driver deltas are monthly-grain).
      return harvestGrain === 'month'
        ? (driverModelCashStatement(direct.statement) as QuickBooksFpaStatementRender | null)
        : direct.statement
    }
    const [high, expected, low] = await Promise.all([build('high'), build('expected'), build('low')])
    return expected ? { high, expected, low } : null
  }

  const exportForecastExcel = async () => {
    const wb = trimStr(workbookId)
    if (!wb) {
      setExcelExportStatus('Select a workbook before exporting.')
      return
    }
    const companyName = trimStr(bootstrap?.company_name) || 'CFO Core'
    const anchorPeriod = actualAnchorPeriod || selectedEndPeriod || null
    setExcelExportBusy(true)
    setExcelExportStatus('Building the world-class workbook…')
    try {
      const fileName = buildCfoForecastExcelFileName({ companyName, anchorPeriod, grain: 'Monthly' })
      // Harvest the online model's displayed per-case cash (the workbook baseline) at all
      // three grains: monthly drives the statements; the timing-aware weekly/daily direct
      // cash (dated AR/AP + recurring payroll/rent/loan) drives the 13-week and Daily sheets.
      const [harvestedCashByCase, harvestedWeekly, harvestedDaily] = await Promise.all([
        harvestDisplayedCashByCase(wb, 'month').catch(() => null),
        harvestDisplayedCashByCase(wb, 'weekly').catch(() => null),
        harvestDisplayedCashByCase(wb, 'daily').catch(() => null),
      ])
      // Self-validate: the harvest's EXPECTED case runs the identical pipeline as the
      // on-screen adjustedModelStatement, so it must reproduce it. If it materially
      // diverges, something drifted — drop the harvest and fall back to the safe
      // Balance-Sheet-derived cash rather than ship numbers that don't match online.
      const forecastCashByCase = (() => {
        const lastForecastEnd = (s: any): number | null => {
          const row = s?.rows?.find((x: any) => x.node_id === 'fg-forecast-cash-ending-cash')
          const vals = (row?.values || []).filter((v: any) => typeof v === 'number' && Number.isFinite(v))
          return vals.length ? (vals[vals.length - 1] as number) : null
        }
        if (!harvestedCashByCase?.expected || !adjustedModelStatement) return harvestedCashByCase
        const harvested = lastForecastEnd(harvestedCashByCase.expected)
        const onScreen = lastForecastEnd(adjustedModelStatement)
        if (harvested == null || onScreen == null) return harvestedCashByCase
        const relDiff = Math.abs(harvested - onScreen) / Math.max(1, Math.abs(onScreen))
        return relDiff <= 0.10 ? harvestedCashByCase : null
      })()
      const input = await assembleCfoForecastWorkbookInput({
        api: appApi,
        workbookId: wb,
        companyName,
        anchorPeriod,
        scenarioKey,
        packageId: selectedForecastPackageId ? forecastPackageId : null,
        futureMonths,
        generatedAtIso: new Date().toISOString(),
        assumptions,
        baselineAssumptions,
        currentStatement: adjustedModelStatement || adjustedStatement || null,
        currentStatementType: statementKind,
        connectionId: actualConnectionId || null,
        layoutVersionId: actualLayoutVersionId || null,
        historyMonths: 24,
        forecastCashByCase,
        weeklyCashByCase: harvestedWeekly,
        dailyCashByCase: harvestedDaily,
        // Credibility layer: backtested accuracy + readiness/vintage honesty.
        forecastVsActualRows: forecastVsActual?.rows ?? null,
        readinessStatus: accuracyReadiness.status || null,
        basisLabels: accuracyReadiness.basisLabels,
        dataAsOf: trimStr(accuracyPackage?.source_window_end || forecastPackageSourceWindowEnd) || anchorPeriod || null,
      })
      if (input.statements.length === 0) {
        setExcelExportStatus('No forecast data is available to export yet. Load a forecast first.')
        return
      }
      const { bytes, warnings } = writeCfoForecastWorkbookExcelBytes(input)
      downloadWorkbookBytes({ fileName, bytes })
      const noteCount = (input.warnings?.length ?? 0)
      setExcelExportStatus(`Exported ${fileName}${noteCount ? ` · see the Methodology sheet for ${noteCount} note${noteCount === 1 ? '' : 's'}` : ''}.`)
      void warnings
    } catch (err: any) {
      setExcelExportStatus(`Excel export failed: ${trimStr(err?.message || err) || 'Unknown error'}`)
    } finally {
      setExcelExportBusy(false)
    }
  }

  const renderForecastStatementControls = (fullscreen: boolean) => (
    <div className={fullscreen ? 'cfo-financials-fullscreen-controls' : 'cfo-financials-inline-controls'}>
      {fullscreen ? <div className="cfo-shell-crumb cfo-financials-fullscreen-crumb"><b>Forecast Studio</b> · {statementLabel(statementKind)}</div> : null}
      <CfoShellSelectChip
        label="Grain"
        value={grain}
        onChange={(value) => setGrain(value)}
        options={GRAIN_OPTIONS}
        ariaLabel="Select forecast grain"
        className="cfo-financials-select-chip"
      />
      <CfoShellSelectChip
        label="Statement"
        value={statementKind}
        onChange={(value) => handleStatementKindChange(value as StatementKind)}
        options={STATEMENT_OPTIONS}
        ariaLabel="Select forecast statement"
        className="cfo-financials-select-chip"
      />
      <CfoShellSelectChip
        label="Case"
        value={valueCase}
        onChange={(value) => setValueCase(value)}
        options={VALUE_CASE_OPTIONS}
        ariaLabel="Select forecast case"
        className="cfo-financials-select-chip"
      />
      <CfoShellSelectChip
        label="Units"
        options={UNIT_OPTIONS}
        value={unitMode}
        onChange={(value) => setUnitMode(value as UnitMode)}
        ariaLabel="Select forecast statement units"
        className="cfo-financials-select-chip"
        menuAlign="end"
      />
      {flaggedNodeCount || flaggedOnly ? (
        <button
          type="button"
          className="cfo-shell-chip cfo-forecast-flagged-chip"
          aria-pressed={flaggedOnly}
          onClick={() => setFlaggedOnly((value) => !value)}
        >
          ⚑ Flagged only{flaggedNodeCount ? ` (${flaggedNodeCount})` : ''}
        </button>
      ) : null}
      <label className="cfo-shell-chip" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span>Actual history</span>
        <input
          type="number"
          min={0}
          value={actualHistoryPeriods}
          aria-label={actualHistoryAriaLabel}
          onChange={(event) => setActualHistoryPeriods(event.target.value)}
          style={{ width: 58 }}
        />
        <span>{actualHistoryUnitLabel}</span>
      </label>
      {grain === 'month' ? (
        <>
          <label className="cfo-shell-chip" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>Forecast</span>
            <input
              type="number"
              min={1}
              max={FORECAST_STUDIO_MAX_FORECAST_MONTHS}
              value={futureMonths}
              aria-label="Forecast months"
              onChange={(event) => setFutureMonths(Math.max(1, Math.min(FORECAST_STUDIO_MAX_FORECAST_MONTHS, Math.floor(Number(event.target.value) || 1))))}
              style={{ width: 58 }}
            />
            <span>mo</span>
          </label>
        </>
      ) : null}
      <button
        type="button"
        className="cfo-shell-chip"
        disabled={excelExportBusy || !adjustedStatement || !proFeatures}
        title={proFeatures ? 'Export the full current forecast to Excel' : 'The Excel model is part of Pro'}
        onClick={() => { if (proFeatures) void exportForecastExcel() }}
      >
        <Download size={15} aria-hidden="true" />
        {excelExportBusy ? 'Exporting' : 'Excel'}
      </button>
      <button
        type="button"
        className="btn icon cfo-statement-fullscreen-toggle"
        aria-label={statementFullscreen ? 'Exit full screen' : 'Open full screen'}
        title={statementFullscreen ? 'Exit full screen' : 'Full screen'}
        onClick={() => setStatementFullscreen((value) => !value)}
      >
        {statementFullscreen ? <Minimize2 size={17} aria-hidden="true" /> : <Maximize2 size={17} aria-hidden="true" />}
      </button>
    </div>
  )

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="forecast"
      activeSection={SURFACE}
      runtimeProfile={runtimeProfile}
      eyebrow="Plan"
      title="Forecast Studio"
      hidePageHead
      crumb={
        <>
          <b>Forecast Studio</b> · Daily, weekly, monthly
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={actualAnchorPeriod || undefined}
      onPeriodChange={onSelectedEndPeriodChange}
      periodAriaLabel={nativeReportingAnchor ? 'Select complete forecast reporting month' : 'Select reconciled forecast anchor'}
      comparisonLabel={comparisonOptions.find((option) => option.id === comparisonKind)?.label || 'vs. prior month'}
      comparisonOptions={comparisonOptions}
      comparisonValue={comparisonKind}
      onComparisonChange={(value) => onComparisonKindChange?.(value as MetricComparisonKind)}
      comparisonAriaLabel="Select CFO Core comparison period"
      cacheState={forecastPageCacheState}
      pageLoading={forecastDisplayLoading}
      pageLoadingLabel="Loading Forecast Studio..."
      copilotSubtitle="forecasting this model"
      copilotPlaceholder="Ask about the forecast..."
      copilotIntro="You're in Forecast Studio. Ask me to create scenarios, explain drivers, or show how daily cash rolls up to the monthly forecast."
      askPlaceholder="Ask about the forecast..."
      suggestedPrompts={['Improve DSO by 5 days', 'Show low cash scenario', 'What drives the next 13 weeks?']}
      getComparisons={() => comparisonsRef.current}
      instantCards={forecastInstantCards}
      pageFactsFingerprint={forecastPageFactsFingerprint}
      openGlDetail={openGlDetail}
      duckDbOpts={duckDbOpts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
    >
      {() => (
        <>
          {glDetailModal}
          {!statementFullscreen ? <CfoControlRow>{renderForecastStatementControls(false)}</CfoControlRow> : null}

          {!statementFullscreen && workbookId ? <ActivePlanVariancePanel appApi={appApi} workbookId={workbookId} /> : null}

          {!statementFullscreen ? (
            <CfoSurfaceBeliefs
              appApi={appApi}
              workbookId={workbookId}
              filter={(c) => c.directiveKind.startsWith('forecast.')}
              heading="What the AI assumed about your forecast"
            />
          ) : null}

          <section className="hero rise">
            <div className="hero-top">
              <div>
                <h1>{endingCash == null ? nearTermCash == null ? 'Forecast Studio' : `${formatCurrencyCompact(nearTermCash)} modeled net cash` : `${formatCurrencyCompact(endingCash)} projected ending cash`}</h1>
                <p>
                  {statementLabel(statementKind)} forecast across {periodCount} {grain === 'month' ? 'months' : grain === 'weekly' ? 'weeks' : 'days'}.
                  {grain === 'month'
                    ? nativeReportingAnchor
                      ? ' Actual columns stop at the last complete reporting month; forecast columns start after it.'
                      : ' Actual columns stop at the reconciled anchor; forecast columns start after it.'
                    : ' Daily and weekly cash stay tied to the monthly three-statement control package.'}
                  {forecastSubtitleAnchor}
                </p>
                <span className="tag" title={forecastBandLabel(trendBandSource)}>
                  {trendBandSource === 'component-envelope' ? 'Expected · high · low component range envelope' : `Expected · high · low driver bands · ${trendBandSource === 'calibrated' ? 'calibrated range' : 'estimated range'}`}
                </span>
                {accuracySummary?.plainLanguage ? (
                  <span className="cfo-forecast-accuracy-wrap">
                    <button
                      type="button"
                      className="cfo-shell-chip"
                      aria-expanded={accuracyOpen}
                      aria-haspopup="dialog"
                      onClick={() => setAccuracyOpen((value) => !value)}
                    >
                      {accuracySummary.plainLanguage}
                    </button>
                    {accuracyOpen ? (
                      <div className="cfo-forecast-accuracy-popover" role="dialog" aria-label="Forecast accuracy by statement">
                        <ul>
                          {accuracySummary.byStatement.map((item) => (
                            <li key={item.statementType}>
                              <span>
                                {forecastAccuracyStatementLabel(item.statementType)} · {item.monthCount} mo
                                {item.bias != null ? ` · bias ${(item.bias * 100).toFixed(1)}%` : ''}
                              </span>
                              <b>
                                {item.wape != null
                                  ? `WAPE ${(item.wape * 100).toFixed(1)}%`
                                  : `median ${(item.medianAbsVariancePct * 100).toFixed(1)}%`}
                              </b>
                            </li>
                          ))}
                          {accuracySummary.intervalHitRate != null ? (
                            <li>
                              <span>actuals inside forecast range</span>
                              <b>{Math.round(accuracySummary.intervalHitRate * 100)}%</b>
                            </li>
                          ) : null}
                        </ul>
                      </div>
                    ) : null}
                  </span>
                ) : null}
              </div>
            </div>
            <figure className="figure">
              <div className="fig-head">
                <span className="fnum">Package</span>
                <span className="ftitle">{packageLabel}</span>
                <span className="fmeta">{validationStatus}</span>
              </div>
              {forecastTrendMetrics.length ? (
                <div className="cfo-forecast-trends" aria-label="Forecast cash trend lines">
                  {forecastTrendMetrics.map((metric) => (
                    <ForecastTrendPanel
                      key={metric.id}
                      metric={metric}
                      baselineValues={baselineTrendValuesByMetric[metric.id]}
                      bandSource={trendBandSource}
                    />
                  ))}
                </div>
              ) : null}
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                <div>
                  <strong>{periodCount}</strong>
                  <p style={{ margin: 0, color: 'var(--muted)' }}>{grain === 'weekly' ? 'weeks' : grain === 'daily' ? 'days' : 'months'} in view</p>
                </div>
                <div>
                  <strong>{rowCount}</strong>
                  <p style={{ margin: 0, color: 'var(--muted)' }}>line items</p>
                </div>
                <div>
                  <strong>{forecastBasisLabel(forecast?.validation?.rollup_basis)}</strong>
                  <p style={{ margin: 0, color: 'var(--muted)' }}>basis</p>
                </div>
              </div>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', marginTop: 12 }}>
                <div>
                  <strong>{formatCurrencyCompact(beginningCash)}</strong>
                  <p style={{ margin: 0, color: 'var(--muted)' }}>beginning cash at anchor</p>
                </div>
                <div>
                  <strong>{formatCurrencyCompact(nearTermCash)}</strong>
                  <p style={{ margin: 0, color: 'var(--muted)' }}>modeled net cash change</p>
                </div>
                <div>
                  <strong>{formatCurrencyCompact(endingCash)}</strong>
                  <p style={{ margin: 0, color: 'var(--muted)' }}>projected ending cash</p>
                </div>
              </div>
              <CfoAiRead>
                {packageBuildBusy
                  ? 'Your forecast is being prepared now. You can keep working; this page will notify you and update automatically as soon as it is ready.'
                  : forecastPackageEmpty
                    ? 'There’s no forecast to show yet. Rebuild it below and this page will fill in as soon as it’s ready.'
                    : forecastUsesMaterializedArtifacts
                      ? 'This forecast is built from your synced books and refreshes automatically as new months close.'
                      : 'This forecast is built from your most recent closed month and refreshes automatically as your books sync.'}
                {' '}{currentSource ? 'Driver edits request fixed-model scoring; explicit Build fits a new reviewable model.' : 'Driver edits apply an immediate what-if; rerunning the model saves them.'}
              </CfoAiRead>
              {forecastPackageEmpty ? (
                <button
                  type="button"
                  className="btn"
                  style={{ justifySelf: 'start', marginTop: 10 }}
                  disabled={nativeWriteBusy}
                  onClick={() => void requestForecastBuild().catch((err) => setError(String(err?.message || err)))}
                >
                  Build forecast now
                </button>
              ) : null}
            </figure>
          </section>

          {currentSource ? <div className="cfo-shell-notice" role="status">
            Current native actuals through {currentSource.actual_period_ends.at(-1) || 'unavailable'}.
            {' '}{currentForecastRangeNotice(currentSource)}
            {' '}{currentSource.forecast_cash_flow_qualified ? 'Future cash-flow controls use the approved explicit movement policy; they are separate from native historical observations. Dated timing and GL proof remain unavailable.' : 'Modeled cash-flow, dated timing and GL proof remain unavailable in this read.'}
            {!currentSource.model_available ? <button type="button" className="btn" disabled={nativeWriteBusy} onClick={() => void requestForecastBuild().catch(err => setError(String(err?.message || err)))}>Fit model for review</button> : null}
            {currentPreviewPending ? ' What-if scoring is pending; future values are withheld.' : currentPreviewError ? ` What-if unavailable: ${currentPreviewError}` : ''}
          </div> : null}
          {currentSource?.policy_authoring && workbookId ? <NativeEconomicPolicyEditor appApi={appApi} workbookId={workbookId}
            source={currentSource.policy_authoring} binding={forecastBinding} externalBusy={nativeWriteBusy}
            writePending={() => nativeWritePending.current} onWritePending={setNativeWritePending}
            onBuild={message => { setSaving(message); setEnsureRetryNonce(nonce => nonce + 1) }} /> : null}
          {currentSource && workbookId ? <QuickBooksForecastModelReview appApi={appApi} workbookId={workbookId}
            refreshKey={ensureRetryNonce} onReviewSaved={() => setEnsureRetryNonce(value => value + 1)} /> : null}
          <div className="section-title rise">
            <h2 className="ff-guide">Forecast statement</h2>
            <span className="meta">periods across the top · accounts on the left</span>
          </div>
          <div className="cfo-forecast-scenario-row rise" style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
            <div role="group" aria-label="Forecast scenario" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              {['base', 'upside', 'downside'].map((key) => {
                const published = availableScenarioKeys.includes(key)
                const active = scenarioKey === key
                return (
                  <button
                    key={key}
                    type="button"
                    className="cfo-shell-chip"
                    aria-pressed={active}
                    disabled={!published}
                    title={published ? undefined : 'This forecast package has no published lines for this scenario. Rerun Forecast ML.'}
                    style={active ? { fontWeight: 700 } : undefined}
                    onClick={() => handleScenarioSelect(key)}
                  >
                    {scenarioChipLabel(key, scenarioBandMeta)}
                  </button>
                )
              })}
              {whatIfLabel ? (
                <span className="cfo-shell-chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span>What if: {whatIfLabel}</span>
                  <button
                    type="button"
                    className="btn icon"
                    aria-label="Revert what-if drivers to the model baseline"
                    title="Revert to model baseline"
                    onClick={resetAllDrivers}
                  >
                    ↺
                  </button>
                </span>
              ) : null}
            </div>
            <form
              onSubmit={handleWhatIfSubmit}
              style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}
            >
              <input
                type="text"
                value={whatIfText}
                placeholder="+ What if… e.g. 'growth at 12%' or 'collect in 45 days'"
                aria-label="Describe a what-if driver change"
                style={{ minWidth: 280 }}
                onChange={(event) => {
                  setWhatIfText(event.target.value)
                  if (whatIfHint) setWhatIfHint('')
                }}
              />
              <button type="submit" className="btn">Apply</button>
              {whatIfHint ? <span className="meta" role="status">{whatIfHint}</span> : null}
            </form>
          </div>
          <CfoDepthSection
            title="Forecast statement grid"
            showLabel="Show forecast statement"
            defaultOpen
            hideToggle
            hideHeader
            surface={SURFACE}
            workbookId={workbookId}
          >
            {() => {
              const statementFrame = (
                <div className={statementFullscreen ? 'cfo-shell cfo-financials-statement-frame fullscreen' : 'cfo-financials-statement-frame'}>
                  {packageBuild ? (
                    <CfoForecastBuildProgress
                      build={packageBuild}
                      priorForecastLabel={priorForecastLabel}
                      beyondBuiltCoverage={selectedBeyondBuiltCoverage}
                      canPickPriorMonth={Boolean(onSelectedEndPeriodChange)}
                      onAction={handleBuildAction}
                    />
                  ) : null}
                  {localCacheWarning ? <div className="cfo-shell-notice" role="status">{localCacheWarning}</div> : null}
                  {cashDegradedNotice ? (
                    <div className="cfo-shell-notice warning" role="status">
                      <b>{cashDegradedNotice}</b>
                      <button type="button" className="btn" disabled={nativeWriteBusy} onClick={() => void requestForecastBuild().catch((err) => setError(String(err?.message || err)))}>
                        Rebuild forecast package
                      </button>
                    </div>
                  ) : null}
                  {staleDeltas.length ? (
                    <div className="cfo-shell-notice warning cfo-forecast-stale-deltas" role="status">
                      <div className="cfo-forecast-stale-deltas-main">
                        <span>
                          <b>{staleDeltas.length}</b> of your edits reference{staleDeltas.length === 1 ? 's' : ''} lines no longer in this forecast.
                        </span>
                        {staleReviewOpen ? (
                          <ul className="cfo-forecast-stale-deltas-list">
                            {staleDeltas.map((delta) => (
                              <li key={delta.id}>
                                <span>{staleDeltaLabel(delta)}</span>
                                <button
                                  type="button"
                                  className="btn"
                                  disabled={Boolean(staleDeltaBusyId)}
                                  onClick={() => void deleteStaleDelta(delta)}
                                >
                                  {staleDeltaBusyId === delta.id ? 'Removing…' : 'Remove edit'}
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                      <button type="button" className="btn" aria-expanded={staleReviewOpen} onClick={() => setStaleReviewOpen((open) => !open)}>
                        {staleReviewOpen ? 'Hide' : 'Review'}
                      </button>
                    </div>
                  ) : null}
                  {error && !(grain === 'month' && !proFeatures) ? <div className="cfo-shell-notice error">{error}</div> : null}
                  {actualHistoryError ? <div className="cfo-shell-notice error">{actualHistoryError}</div> : null}
                  {profitAndLossFormulaIntegrityNotice ? (
                    <div className="cfo-shell-notice error cfo-forecast-withheld" role="alert">
                      <b>{profitAndLossFormulaIntegrityNotice.headline}</b>
                      <span>{profitAndLossFormulaIntegrityNotice.detail}</span>
                      <span>{profitAndLossFormulaIntegrityNotice.nextStep}</span>
                      <button
                        type="button"
                        className="btn"
                        disabled={packageBuildBusy || nativeWriteBusy}
                        onClick={() => void requestForecastBuild().catch((err) => setError(String(err?.message || err)))}
                      >
                        {packageBuildBusy ? 'Rebuilding…' : 'Rebuild forecast'}
                      </button>
                    </div>
                  ) : null}
                  {statementDiff?.hasChanges ? (
                    <div className="cfo-forecast-diff-strip" role="status" aria-live="polite">
                      <span>vs model baseline:</span>
                      {statementDiff.endingCashDelta != null ? <b>ending cash {formatSignedCurrency(statementDiff.endingCashDelta)}</b> : null}
                      {statementDiff.netIncomeDelta != null ? <b>net income {formatSignedCurrency(statementDiff.netIncomeDelta)}</b> : null}
                      <span>{statementDiff.changedCellCount} forecast {statementDiff.changedCellCount === 1 ? 'cell' : 'cells'} adjusted</span>
                    </div>
                  ) : null}
                  {forecastScenarioKey !== 'base' && scenarioCompareDiff ? (
                    <div className="cfo-forecast-diff-strip cfo-forecast-scenario-compare" role="status" aria-live="polite">
                      <span>{scenarioChipLabel(forecastScenarioKey, null)} vs base:</span>
                      {scenarioCompareDiff.endingCashDelta != null ? <b>ending cash {formatSignedCurrency(scenarioCompareDiff.endingCashDelta)}</b> : null}
                      {scenarioCompareDiff.netIncomeDelta != null ? <b>net income {formatSignedCurrency(scenarioCompareDiff.netIncomeDelta)}</b> : null}
                      <span>base overlaid as the dotted trend baseline</span>
                    </div>
                  ) : null}
                  {serverDriverPreview && serverPreviewRefinedCellCount > 0 ? (
                    <div role="status" style={{ color: 'var(--muted)', fontSize: 12, margin: '4px 0' }}>
                      preview refined by forecast engine ({serverDriverPreview.computedInMs}ms)
                    </div>
                  ) : null}
                  <div aria-busy={packageBuildBusy ? 'true' : undefined}>
                  {grain === 'month' && !proFeatures ? (
                    <CfoProUpgradeNotice appApi={appApi} workbookId={workbookId} title="The 24-month forecast is part of Pro">
                      Project all three statements — P&amp;L, balance sheet, and cash flow — 24 months out, reconciled and rebuilt nightly. It’s part of Pro; your 13-week cash view stays free on Essentials.
                    </CfoProUpgradeNotice>
                  ) : statementWithheldForIntegrity ? (
                    // The alert above already explains the hold. Rendering the table
                    // here with a null statement produced a "loading…" placeholder
                    // that never resolved, so readers waited on a spinner for a
                    // statement we had already decided not to show.
                    null
                  ) : (
                  <CfoStatementTable
                    key={`${statementFullscreen ? 'forecast-statement-fullscreen' : 'forecast-statement-normal'}-${grain}-${statementKind}-${valueCase}-${actualAnchorPeriod || 'active'}-${actualHistoryPeriods}-${futureMonths}`}
                    statement={statementForTable || null}
                    loading={(forecastDisplayLoading || actualLoading || actualBasisLoading) && !adjustedStatement}
                    loadingLabel={actualLoading || actualBasisLoading || directCashActualBasisPending ? 'Loading actual history columns…' : 'Loading forecast statement…'}
                    percentOfSales={unitMode === 'percent'}
                    showCurrencyAndPercent={unitMode === 'currency_percent'}
                    maxPeriods={grain === 'month' ? actualHistoryPeriods + futureMonths : adjustedStatement?.periods.length || forecastStatementForDisplay?.periods.length || 120}
                    maxRows={600}
                    defaultCollapsed
                    fullscreen={statementFullscreen}
                    toolbarLeading={statementFullscreen ? renderForecastStatementControls(true) : null}
                    hideBlankRows
                    comparisonMode="rolling_12"
                    periodHeaderFormatter={periodHeaderFormatter}
                    cellDecorator={statementCellDecorator}
                    onExplainCell={grain === 'month' ? openExplainForCell : undefined}
                    rowBadge={statementRowBadge}
                    layoutNodes={actualLayout?.nodes || null}
                  />
                  )}
                  </div>
                  {excelExportStatus ? (
                    <p className={excelExportStatus.startsWith('Excel export failed') ? 'cfo-statement-export-status error' : 'cfo-statement-export-status'}>
                      {excelExportStatus}
                    </p>
                  ) : null}
                  {explainTarget ? (
                    <CfoForecastExplainDrawer
                      nodeLabel={explainTarget.nodeLabel}
                      period={explainTarget.period}
                      scenarioKey={forecastScenarioKey}
                      line={explainLine}
                      loading={explainLoading}
                      limitedDetail={explainLimited}
                      error={explainError}
                      history={explainHistory}
                      reviewBusy={explainReviewBusy}
                      reviewStatus={explainReviewStatus}
                      onSetReviewState={(state) => void setExplainReviewState(state)}
                      onClose={() => setExplainTarget(null)}
                    />
                  ) : null}
                </div>
              )
              return statementFullscreen && typeof document !== 'undefined'
                ? createPortal(statementFrame, document.body)
                : statementFrame
            }}
          </CfoDepthSection>

          <div className="section-title rise">
            <h2 className="ff-guide">Driver assumptions</h2>
            {anyDriverEdited ? (
              <button type="button" className="btn" onClick={resetAllDrivers}>Reset all drivers</button>
            ) : null}
            <span className="meta">
              {currentSource
                ? saving || 'authored assumptions for the selected scenario'
                : forecastScenarioKey !== 'base'
                  ? 'driver edits model against the base scenario — switch to Base to see them applied'
                  : saving || 'autosaved to active forecast'}
            </span>
          </div>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
            {[
              ['sales_growth_pct', 'Sales growth', '%', 100],
              ['gross_margin_pct', 'Gross margin', '%', 100],
              ['dso_days', 'DSO days', 'days', 1],
              ['dpo_days', 'DPO days', 'days', 1],
              ['opex_pct', 'Opex', '%', 100],
              ['seasonality_strength', 'Seasonality', 'x', 1],
            ].map(([key, label, suffix, scale]) => {
              const value = driverNumber(displayAssumptions, String(key))
              const display = Number(value) * Number(scale)
              const baselineDisplay = Number(driverNumber(baselineAssumptions, String(key))) * Number(scale)
              const edited = isDriverOverride(displayAssumptions, String(key))
              return (
                <label key={String(key)} className="cfo-shell-chip" style={{ display: 'grid', gap: 6, justifyItems: 'start' }}>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {label}
                    {edited ? <span className="tag">Edited</span> : null}
                    {edited ? (
                      <button
                        type="button"
                        className="btn icon"
                        aria-label={`Revert ${label} to model baseline`}
                        title={`Revert to auto: ${Number(baselineDisplay.toFixed(2))}${suffix === '%' ? '%' : ` ${suffix}`}`}
                        onClick={(event) => {
                          event.preventDefault()
                          revertDriver(String(key))
                        }}
                      >
                        ↺
                      </button>
                    ) : null}
                  </span>
                  <input
                    type="number"
                    step={suffix === 'days' ? 1 : 0.5}
                    value={Number(display.toFixed(2))}
                    aria-label={`${label} assumption`}
                    onChange={(event) => saveDriver(String(key), Number(event.target.value) / Number(scale))}
                    style={{ width: '100%' }}
                  />
                  <small style={{ color: 'var(--muted)' }}>
                    {suffix} · auto: {Number(baselineDisplay.toFixed(2))}{suffix === '%' ? '%' : ''}
                  </small>
                </label>
              )
            })}
          </div>

          <div className="section-title rise">
            <h2 className="ff-guide">Autopilot scenarios</h2>
            <button type="button" className="btn" onClick={() => void runAutopilot()}>Refresh scenarios</button>
          </div>
          {loading && !autopilot ? (
            <CfoCardSkeletons count={3} label="Loading forecast scenario cards…" />
          ) : (
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              {(autopilot?.scenarios || []).map((scenario) => (
                <article key={scenario.id} className="cfo-shell-notice">
                  <strong>{scenario.label}</strong>
                  <p style={{ margin: '6px 0', color: 'var(--muted)' }}>{scenario.summary}</p>
                  <span className="tag">{formatCurrencyCompact(Number(scenario.expected_cash_impact) || 0)} potential cash impact</span>
                </article>
              ))}
            </div>
          )}

          {classicEscapes ? (
            <CfoDepthSection
              title="Classic Forecast ML"
              meta="Diagnostics, model runs, backtests, feedback"
              showLabel="Open forecast diagnostics"
              surface={SURFACE}
              workbookId={workbookId}
            >
              {() => (
                <QuickBooksForecastInsightsWorkspace appApi={appApi} workerClient={workerClient} workbookId={workbookId} />
              )}
            </CfoDepthSection>
          ) : null}

          {!proFeatures ? (
            <>
              <div className="section-title rise">
                <h2 className="ff-guide">Pacing vs forecast</h2>
                <span className="meta">Part of Pro</span>
              </div>
              <article className="cfo-shell-notice" style={{ marginBottom: 'var(--s4)' }}>
                <strong>Know on day 12 whether the month is on track</strong>
                <p style={{ margin: '6px 0', color: 'var(--muted)' }}>
                  Pro compares this period’s actuals to its forecast while the period is still in progress — revenue, expenses, profit, and cash pace, each flagged ahead or behind.
                </p>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => {
                    const wb = trimStr(workbookId)
                    if (wb) startCfoCheckout(appApi, wb, 'pro', 'monthly').catch(() => {})
                  }}
                >
                  Upgrade to Pro — $349/mo
                </button>
              </article>
            </>
          ) : (pacingLoading || pacingModel.available) ? (
            <>
              <div className="section-title rise">
                <h2 className="ff-guide">Pacing vs forecast</h2>
                <span className="meta">
                  {grain === 'weekly' || grain === 'daily' ? 'Week-to-date' : 'Month-to-date'} actuals vs this {grain === 'weekly' || grain === 'daily' ? 'week' : 'month'}’s forecast
                </span>
              </div>
              {pacingLoading && !pacingModel.available ? (
                <CfoCardSkeletons count={5} label="Loading pacing…" />
              ) : (
                <ForecastPacingPanel model={pacingModel} error={pacingError} />
              )}
            </>
          ) : null}

          {classicEscapes ? (
            <button type="button" className="btn ghost" style={{ marginTop: 'var(--s4)' }} onClick={onOpenClassic}>
              Open forecast diagnostics
            </button>
          ) : null}
        </>
      )}
    </CfoInteriorPage>
  )
}

export default CfoForecastPage
